import React, { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import emailjs from '@emailjs/browser';
import { LanguageProvider, useLanguage } from './LanguageContext';
import { BlogIndexContent, BlogArticleContent } from './components/BlogContent';
import { PartenairesContent } from './components/PartenairesContent';
import { 
  User, 
  PawPrint, 
  Users, 
  Video, 
  Menu, 
  X, 
  Phone,
  Mail,
  MapPin,
  Heart,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Moon,
  Sun,
  Shield,
  Lock,
  CheckCircle,
  Award,
  Download,
  Eye,
  EyeOff
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";

// Custom hook for counting animation
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) {
      // Start immediately
      setHasStarted(true);
    }
  }, [startOnView]);

  useEffect(() => {
    if (startOnView && ref.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasStarted) {
            setHasStarted(true);
          }
        },
        { threshold: 0.5 }
      );
      observer.observe(ref.current);
      return () => observer.disconnect();
    }
  }, [startOnView, hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime = null;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [hasStarted, end, duration]);

  return { count, ref };
};

// Theme Context
const ThemeContext = React.createContext();

const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Image constants
const IMAGES = {
  grandfather: "https://images.unsplash.com/photo-1601779805802-d8363cd70ccc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxlbGRlcmx5JTIwZ3JhbmRmYXRoZXIlMjBwb3J0cmFpdCUyMHZpbnRhZ2UlMjBzZXBpYXxlbnwwfHx8fDE3NzM1MTU1NjR8MA&ixlib=rb-4.1.0&q=85",
  mother: "https://images.pexels.com/photos/3768131/pexels-photo-3768131.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  grandmother: "https://images.unsplash.com/photo-1496672254107-b07a26403885?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxncmFuZG1vdGhlciUyMGVsZGVybHklMjB3b21hbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc3MzUxNTU2Nnww&ixlib=rb-4.1.0&q=85",
  dog: "https://images.unsplash.com/photo-1597205819209-a999c84568d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxnb2xkZW4lMjByZXRyaWV2ZXIlMjBkb2clMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzM1MTU1Njd8MA&ixlib=rb-4.1.0&q=85",
  cat: "https://images.unsplash.com/photo-1741700840853-6c35a47b4e2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxjYXQlMjBwb3J0cmFpdCUyMHBlYWNlZnVsfGVufDB8fHx8MTc3MzUxNTU2N3ww&ixlib=rb-4.1.0&q=85",
  friend: "https://images.unsplash.com/photo-1652527318479-389de3ecd048?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxlbGRlcmx5JTIwZ3JhbmRmYXRoZXIlMjBwb3J0cmFpdCUyMHZpbnRhZ2UlMjBzZXBpYXxlbnwwfHx8fDE3NzM1MTU1NjR8MA&ixlib=rb-4.1.0&q=85"
};

// Preloader photos - mix of people and animals (sync with current memorialData)
const PRELOADER_PHOTOS = [
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop",   // Françoise
  "https://images.unsplash.com/photo-1558788353-f76d92427f16?w=100&h=100&fit=crop",   // Max (dog)
  "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop", // Louis
  "https://images.unsplash.com/photo-1545167622-3a6ac756afa4?w=100&h=100&fit=crop",   // Raymond
  "https://images.unsplash.com/photo-1647806422508-0322f33e270b?w=100&h=100&fit=crop", // Minou (cat)
  "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop",   // Pierre
  "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&h=100&fit=crop", // Maurice
  "https://images.unsplash.com/photo-1629740067905-bd3f515aa739?w=100&h=100&fit=crop", // Belle (animal)
  "https://images.pexels.com/photos/13183281/pexels-photo-13183281.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=1&fit=crop", // Tom
  "https://images.unsplash.com/photo-1608032364895-0da67af36cd2?w=100&h=100&fit=crop", // Félix (cat)
  "https://images.unsplash.com/photo-1629540266304-fff9c67b7660?w=100&h=100&fit=crop", // Catherine
  "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=100&h=100&fit=crop", // Buddy (dog)
  "https://images.unsplash.com/photo-1498757581981-8ddb3c0b9b07?crop=entropy&cs=srgb&fm=jpg&w=100&h=100&fit=crop", // Yvonne
  "https://images.unsplash.com/photo-1544568100-847a948585b9?w=100&h=100&fit=crop",   // Médor (dog)
  "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop", // Bernadette
  "https://images.unsplash.com/photo-1534751516642-a1af1ef26a56?w=100&h=100&fit=crop"  // Hélène
];

// Preloader Component
const Preloader = ({ onComplete }) => {
  const { t } = useLanguage();
  
  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="preloader" data-testid="preloader">
      <div className="text-center">
        <div className="vintage-frame flex items-center justify-center bg-dark-secondary p-4 overflow-hidden">
          <div className="grid grid-cols-4 gap-1">
            {PRELOADER_PHOTOS.map((photo, index) => (
              <div 
                key={index} 
                className="w-12 h-12 md:w-14 md:h-14 overflow-hidden opacity-80"
                style={{ 
                  animationDelay: `${index * 0.1}s`,
                  animation: 'fadeInPhoto 0.5s ease forwards'
                }}
              >
                <img 
                  src={photo} 
                  alt=""
                  className="w-full h-full object-cover grayscale-[40%] sepia-[30%]"
                />
              </div>
            ))}
          </div>
        </div>
        <h1 className="font-playfair text-2xl md:text-3xl text-sepia tracking-[0.3em] mt-8">
          {t('preloader.title')}
        </h1>
        <p className="font-cormorant text-xl md:text-2xl text-sepia-light italic mt-2">
          {t('preloader.subtitle')}
        </p>
      </div>
    </div>
  );
};

// Birds Animation Component
const BirdsContainer = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const createBird = () => {
      if (!containerRef.current) return;
      
      const bird = document.createElement('div');
      bird.className = 'bird';
      bird.style.top = `${Math.random() * 80 + 10}%`;
      bird.style.animationDuration = `${Math.random() * 10 + 15}s`;
      bird.style.animationDelay = `${Math.random() * 5}s`;
      containerRef.current.appendChild(bird);
      
      setTimeout(() => {
        bird.remove();
      }, 25000);
    };

    for (let i = 0; i < 5; i++) {
      setTimeout(createBird, i * 1000);
    }

    const interval = setInterval(createBird, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div 
      ref={containerRef} 
      className="absolute inset-0 pointer-events-none z-[2]"
      data-testid="birds-container"
    />
  );
};

// Floating Memory Photo Component with real images - Desktop version
const MemoryPhotoDesktop = ({ label, image, floatAnimation }) => (
  <div className={`memory-photo-desktop ${floatAnimation}`}>
    <div className="w-[140px] h-[170px] overflow-hidden bg-white p-2 shadow-lg">
      <img 
        src={image} 
        alt={label}
        className="w-full h-full object-cover grayscale-[30%] sepia-[20%]"
      />
    </div>
    <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 font-cormorant italic text-sm text-sepia whitespace-nowrap bg-white/90 px-2 py-0.5 rounded">
      {label}
    </span>
  </div>
);

// Mobile Memory Photo Row Component
const MobilePhotoRow = ({ photos }) => (
  <div className="flex justify-center gap-3 md:hidden px-4">
    {photos.map((photo, index) => (
      <div 
        key={index}
        className="relative"
        style={{ transform: `rotate(${photo.rotation}deg)` }}
      >
        <div className="w-[80px] h-[100px] sm:w-[90px] sm:h-[110px] overflow-hidden bg-dark-card p-1.5 shadow-lg border border-gold-muted/30">
          <img 
            src={photo.image} 
            alt={photo.label}
            className="w-full h-full object-cover grayscale-[30%] sepia-[20%]"
          />
        </div>
        <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 font-cormorant italic text-[10px] text-sepia whitespace-nowrap bg-dark-card/90 px-1.5 py-0.5 rounded border border-gold-muted/30">
          {photo.label}
        </span>
      </div>
    ))}
  </div>
);

// Language Switcher Component
const LanguageSwitcher = ({ className = "" }) => {
  const { language, toggleLanguage } = useLanguage();
  
  return (
    <button
      onClick={toggleLanguage}
      className={`flex items-center gap-2 hover:opacity-80 transition-opacity ${className}`}
      title={language === 'fr' ? 'Switch to English' : 'Passer en français'}
      data-testid="language-switcher"
    >
      {language === 'fr' ? (
        <>
          <span className="text-lg">🇫🇷</span>
          <span className="text-xs text-cream/60 hidden sm:inline">FR</span>
        </>
      ) : (
        <>
          <span className="text-lg">🇬🇧</span>
          <span className="text-xs text-cream/60 hidden sm:inline">EN</span>
        </>
      )}
    </button>
  );
};

// Navigation Component
const Navigation = ({ onOpenModal, onOpenFAQ }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/jesuis', label: t('nav.monHistoire') },
    { href: '/deroulement', label: t('nav.deroulement') },
    { href: '/services', label: t('nav.services') },
    { href: '/temoignages', label: t('nav.temoignages') },
    { href: '/hommages', label: t('nav.auxEtresChers') },
    { href: '/blog', label: t('nav.blog') },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-dark-bg/95 dark:bg-dark-bg/95 light:bg-cream/95 backdrop-blur-lg py-4 border-b border-sepia/20' 
          : 'bg-dark-bg/90 backdrop-blur-md md:bg-gradient-to-b md:from-dark-bg/95 md:to-transparent dark:md:from-dark-bg/95 light:md:from-cream/95 py-6'
      }`}
      data-testid="navigation"
    >
      <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 flex justify-between items-center">
        <Link 
          to="/" 
          className="flex items-center gap-3 font-playfair text-xl md:text-2xl text-sepia tracking-[0.15em] hover:opacity-80 transition-opacity relative group"
          data-testid="logo"
        >
          <span className="w-10 h-10 md:w-11 md:h-11 border border-gold-muted/50 rounded-full flex items-center justify-center bg-dark-bg">
            <span className="font-playfair text-sm md:text-base text-sepia font-semibold tracking-wide">BM</span>
          </span>
          <span>BRICE MÉDIUM</span>
          <span className="absolute -bottom-1 left-14 right-0 h-px bg-gold-soft transform scale-x-0 group-hover:scale-x-100 transition-transform" />
        </Link>
        
        {/* Desktop Menu */}
        <ul className="hidden md:flex gap-8 items-center">
          {navLinks.map((link) => (
            <li key={link.href}>
              {link.href.startsWith('#') ? (
                <a 
                  href={link.href}
                  className="font-lato text-sm text-cream/80 hover:text-sepia tracking-wider uppercase relative group transition-colors font-light"
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-sepia group-hover:w-full transition-all duration-300" />
                </a>
              ) : (
                <Link 
                  to={link.href}
                  className="font-lato text-sm text-cream/80 hover:text-sepia tracking-wider uppercase relative group transition-colors font-light"
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-sepia group-hover:w-full transition-all duration-300" />
                </Link>
              )}
            </li>
          ))}
          <li>
            <Button 
              onClick={onOpenModal}
              className="bg-sepia text-dark-bg hover:bg-gold-soft font-playfair tracking-wider px-6 py-2 transition-all duration-300 rounded-none attention-pulse"
              data-testid="nav-contact-btn"
            >
              {t('nav.contact')}
            </Button>
          </li>
          <li>
            <LanguageSwitcher />
          </li>
        </ul>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-3 md:hidden">
          {/* Language Switcher Mobile */}
          <LanguageSwitcher />
          <button 
            className="text-sepia relative z-[110]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - Full screen overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="md:hidden fixed inset-0 bg-black/80 z-[9998]"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="md:hidden fixed inset-y-0 right-0 w-4/5 max-w-xs z-[9999] shadow-2xl border-l border-gold-muted/20" style={{backgroundColor: '#0a0a0a'}}>
            <div className="flex justify-end p-6 border-b border-sepia/20">
              <button 
                className="text-sepia hover:text-gold-soft"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <X size={28} />
              </button>
            </div>
            <nav className="px-5 py-6 space-y-3 overflow-y-auto max-h-[calc(100vh-80px)]">
              {navLinks.map((link) => (
                link.href.startsWith('#') ? (
                  <a 
                    key={link.href}
                    href={link.href}
                    className="mobile-nav-link block font-lato text-cream/90 hover:text-sepia hover:border-sepia py-3 px-4 tracking-wider uppercase text-base border border-gold-muted/40 transition-colors"
                    style={{backgroundColor: 'rgba(20, 20, 20, 0.95)'}}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="mobile-nav-link block font-lato text-cream/90 hover:text-sepia hover:border-sepia py-3 px-4 tracking-wider uppercase text-base border border-gold-muted/40 transition-colors"
                    style={{backgroundColor: 'rgba(20, 20, 20, 0.95)'}}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              <button 
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onOpenModal();
                }}
                className="block font-lato text-black bg-sepia hover:bg-sepia-light py-3 px-4 tracking-wider uppercase text-base border border-sepia w-full text-left mt-4 animate-soft-blink font-semibold"
                data-testid="mobile-contact-btn"
              >
                {t('nav.contacterDefunt')}
              </button>
            </nav>
          </div>
        </>
      )}
    </nav>
  );
};

// Hero Section
// Back to Top Button Component
const BackToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 500) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility, { passive: true });
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <button
      onClick={scrollToTop}
      className={`fixed bottom-20 right-6 z-40 w-12 h-12 bg-sepia hover:bg-gold-soft text-dark-bg rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10 pointer-events-none'
      }`}
      aria-label="Retour en haut"
      data-testid="back-to-top"
    >
      <ChevronUp size={24} />
    </button>
  );
};

const HeroSection = ({ onOpenModal, onOpenFAQ }) => {
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    const handleMouseMove = (e) => {
      // Calculate mouse position relative to center of screen (-1 to 1)
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePosition({ x, y });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Photos data for reuse
  const topPhotos = [
    { label: t('hero.photoLabels.grandfather'), image: IMAGES.grandfather, rotation: -5 },
    { label: t('hero.photoLabels.cat'), image: IMAGES.cat, rotation: 4 },
    { label: t('hero.photoLabels.grandmother'), image: IMAGES.grandmother, rotation: -3 }
  ];
  
  const bottomPhotos = [
    { label: t('hero.photoLabels.mother'), image: IMAGES.mother, rotation: 3 },
    { label: t('hero.photoLabels.dog'), image: IMAGES.dog, rotation: -4 },
    { label: t('hero.photoLabels.friend'), image: IMAGES.friend, rotation: 5 }
  ];

  return (
    <section 
      className="min-h-screen relative flex flex-col items-center justify-center bg-dark-bg overflow-hidden pt-20 pb-16 md:pb-0"
      data-testid="hero-section"
    >
      
      {/* Desktop: Floating Memory Photos - 2 à gauche, 2 à droite du texte */}
      <div className="floating-memories absolute inset-0 pointer-events-none z-[1] hidden md:block">
        {/* Photo 1 - Haut Gauche (Maman) : top 15%, left 8% */}
        <div 
          className="memory-photo absolute transition-transform duration-200 ease-out"
          style={{ 
            top: '15%', 
            left: '8%',
            transform: `
              translateY(${-scrollY * 0.5 + mousePosition.y * -15}px) 
              translateX(${scrollY * 0.1 + mousePosition.x * -20}px) 
              rotate(${-8 + mousePosition.x * 3}deg)
              scale(${1 + Math.abs(mousePosition.x) * 0.02})
            `
          }}
        >
          <MemoryPhotoDesktop 
            label={t('hero.photoLabels.mother')} 
            image={IMAGES.mother} 
            floatAnimation="float-animation-1"
          />
        </div>
        {/* Photo 2 - Haut Droite (Minou) : top 20%, right 10% */}
        <div 
          className="memory-photo absolute transition-transform duration-200 ease-out"
          style={{ 
            top: '20%', 
            right: '10%',
            transform: `
              translateY(${-scrollY * 0.3 + mousePosition.y * -12}px) 
              translateX(${scrollY * 0.06 + mousePosition.x * 25}px) 
              rotate(${6 + mousePosition.x * -2}deg)
              scale(${1 + Math.abs(mousePosition.y) * 0.02})
            `
          }}
        >
          <MemoryPhotoDesktop 
            label={t('hero.photoLabels.cat')} 
            image={IMAGES.cat} 
            floatAnimation="float-animation-2"
          />
        </div>
        {/* Photo 3 - Bas Gauche (Grand-père) : bottom 20%, left 12% */}
        <div 
          className="memory-photo absolute transition-transform duration-200 ease-out"
          style={{ 
            bottom: '20%', 
            left: '12%',
            transform: `
              translateY(${-scrollY * 0.7 + mousePosition.y * -18}px) 
              translateX(${scrollY * 0.14 + mousePosition.x * -18}px) 
              rotate(${5 + mousePosition.y * 2}deg)
              scale(${1 + Math.abs(mousePosition.x) * 0.015})
            `
          }}
        >
          <MemoryPhotoDesktop 
            label={t('hero.photoLabels.grandfather')} 
            image={IMAGES.grandfather} 
            floatAnimation="float-animation-3"
          />
        </div>
        {/* Photo 4 - Bas Droite (Rex) : bottom 25%, right 8% */}
        <div 
          className="memory-photo absolute transition-transform duration-200 ease-out"
          style={{ 
            bottom: '25%', 
            right: '8%',
            transform: `
              translateY(${-scrollY * 0.4 + mousePosition.y * -10}px) 
              translateX(${scrollY * 0.08 + mousePosition.x * 22}px) 
              rotate(${-4 + mousePosition.y * -2}deg)
              scale(${1 + Math.abs(mousePosition.y) * 0.02})
            `
          }}
        >
          <MemoryPhotoDesktop 
            label={t('hero.photoLabels.dog')} 
            image={IMAGES.dog} 
            floatAnimation="float-animation-4"
          />
        </div>
      </div>

      {/* Mobile: Top Photos Row */}
      <div className="w-full mb-8 mt-4 animate-fadeInUp">
        <MobilePhotoRow photos={topPhotos} />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center max-w-3xl mx-auto px-4 sm:px-6 flex-1 flex flex-col justify-center">
        <p className="font-lato text-sm md:text-base tracking-[0.4em] text-sepia-light uppercase mb-8 animate-fadeInUp animate-blink">
          {t('hero.badge')}
        </p>
        
        <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal leading-tight mb-6 text-cream animate-fadeInUp-delay-1">
          {t('hero.titleLine1')}<br />{t('hero.titleLine2')} <em className="text-sepia italic">
            {t('hero.titleHighlight')}
          </em>
        </h1>
        
        {/* Mobile version with line breaks */}
        <p className="md:hidden font-cormorant text-xl text-cream/90 mb-10 font-light animate-fadeInUp-delay-2 text-center whitespace-pre-line">
          {t('hero.descriptionMobile')}
        </p>
        
        {/* Desktop/Tablet version */}
        <p className="hidden md:block font-cormorant text-xl text-cream/90 mb-10 font-light animate-fadeInUp-delay-2">
          {t('hero.descriptionDesktop')}
        </p>
        
        <div className="flex flex-col items-center gap-5 animate-fadeInUp-delay-3">
          <Button 
            onClick={onOpenModal}
            className="bg-sepia text-dark-bg hover:bg-gold-soft font-playfair text-base tracking-[0.15em] px-10 py-6 transition-all duration-300 rounded-none hover:-translate-y-0.5 hover:shadow-lg hover:shadow-sepia/20"
            data-testid="hero-cta-btn"
          >
            {t('hero.ctaButton')}
          </Button>
          
          <button 
            onClick={onOpenFAQ}
            className="inline-flex items-center gap-3 px-8 md:px-6 py-4 md:py-3 bg-dark-secondary border border-gold-muted/50 rounded-full font-lato text-base md:text-sm text-sepia hover:bg-dark-card transition-colors cursor-pointer"
          >
            <Heart size={16} className="text-sepia animate-heartbeat" fill="currentColor" />
            <span className="animate-blink">{t('hero.freeMinutes')}</span>
          </button>
        </div>
      </div>

      {/* Mobile: Bottom Photos Row */}
      <div className="w-full mt-8 mb-0 animate-fadeInUp-delay-2">
        <MobilePhotoRow photos={bottomPhotos} />
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
        <ChevronDown size={28} className="text-sepia/40" />
      </div>
    </section>
  );
};

// Transition Section
const TransitionSection = () => (
  <div className="h-8 md:h-12 bg-gradient-to-b from-dark-bg to-dark-secondary relative">
  </div>
);

// About Section
const AboutSection = () => {
  const { count: yearsCount, ref: yearsRef } = useCountUp(23, 2000);
  const { count: consultationsCount, ref: consultationsRef } = useCountUp(11000, 2500);
  const { t } = useLanguage();

  return (
    <section 
      id="about" 
      className="pt-24 pb-8 md:pb-12 bg-dark-secondary relative"
      data-testid="about-section"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-muted/50 to-transparent" />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-12 reveal">
          <p className="font-lato text-base md:text-base tracking-[0.3em] text-sepia-light uppercase mb-4 animate-blink">
            {t('about.badge')}
          </p>
          {/* Mobile version with line break */}
          <h2 className="md:hidden font-playfair text-3xl text-cream mb-4 whitespace-pre-line">
            {t('about.titleMobile')}
          </h2>
          {/* Desktop/Tablet version */}
          <h2 className="hidden md:block font-playfair text-4xl text-cream mb-4">
            {t('about.titleDesktop')}
          </h2>
          {/* Decorative Line */}
          <div className="flex justify-center">
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-sepia to-transparent"></div>
          </div>
        </div>

        {/* Handwritten Letter */}
        <div className="reveal">
          <div className="bg-dark-card p-8 md:p-12 shadow-xl relative transform -rotate-1 mb-12 border border-gold-muted/30">
            <div className="absolute inset-2.5 border border-gold-muted/20 pointer-events-none" />
            
            <div className="font-cormorant text-xl md:text-2xl leading-loose text-cream/90 space-y-6">
              <p className="text-center italic whitespace-pre-line">
                {t('about.letter.intro')}
              </p>
              <p className="text-center">
                {t('about.letter.question')}
              </p>
              <p className="whitespace-pre-line">
                {t('about.letter.passeur')}
              </p>
              {/* Mobile version */}
              <p className="md:hidden whitespace-pre-line">
                {t('about.letter.perceptionMobile')}
              </p>
              {/* Desktop version */}
              <p className="hidden md:block whitespace-pre-line">
                {t('about.letter.perceptionDesktop')}
              </p>
              <p className="whitespace-pre-line">
                {t('about.letter.defunts')}
              </p>
              {/* Mobile version */}
              <p className="md:hidden whitespace-pre-line">
                {t('about.letter.questionsMobile')}
              </p>
              {/* Desktop version */}
              <p className="hidden md:block whitespace-pre-line">
                {t('about.letter.questionsDesktop')}
              </p>
              {/* Mobile version */}
              <p className="md:hidden whitespace-pre-line">
                {t('about.letter.experienceMobile')}
              </p>
              {/* Desktop version */}
              <p className="hidden md:block whitespace-pre-line">
                {t('about.letter.experienceDesktop')}
              </p>
              <p className="text-center italic whitespace-pre-line">
                {t('about.letter.moment')}
              </p>
              <p>
                {t('about.letter.accompagnement')}
              </p>
              <p className="text-center">
                {t('about.letter.conclusion')}
              </p>
            </div>
            
            <div className="font-playfair italic text-xl text-sepia mt-8 text-right">
              {t('about.letter.signature')}
            </div>
          </div>
        </div>

        {/* Offer Banner - 10 minutes offertes */}
        <div className="reveal mt-16 mb-12">
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-dark-card to-dark-bg border border-gold-muted/40 p-8 md:p-10 relative overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-sepia/20 hover:border-sepia/60 group cursor-pointer">
            {/* Decorative corner elements */}
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-sepia/50 transition-all duration-300 group-hover:w-20 group-hover:h-20 group-hover:border-sepia"></div>
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-sepia/50 transition-all duration-300 group-hover:w-20 group-hover:h-20 group-hover:border-sepia"></div>
            
            {/* BM Logo */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto border-2 border-sepia rounded-full flex items-center justify-center bg-dark-bg transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-sepia/30">
                <span className="font-playfair text-2xl text-sepia font-semibold tracking-wide">BM</span>
              </div>
              {/* Mobile version */}
              <h3 className="md:hidden font-playfair text-2xl text-sepia mt-4 tracking-wide whitespace-pre-line">
                {t('about.offer.titleMobile')}
              </h3>
              {/* Desktop version */}
              <h3 className="hidden md:block font-playfair text-3xl text-sepia mt-4 tracking-wide">
                {t('about.offer.titleDesktop')}
              </h3>
            </div>
            
            {/* Mobile version */}
            <p className="md:hidden font-cormorant text-xl text-cream/90 text-center italic mb-8 whitespace-pre-line">
              {t('about.offer.subtitleMobile')}
            </p>
            {/* Desktop version */}
            <p className="hidden md:block font-cormorant text-xl text-cream/90 text-center italic mb-8">
              {t('about.offer.subtitleDesktop')}
            </p>
            
            {/* Mobile version - stacked layout */}
            <div className="md:hidden space-y-4 max-w-lg mx-auto">
              <div className="text-center">
                <span className="text-sepia text-lg block mb-2">👉</span>
                <p className="font-cormorant text-lg text-cream/80">
                  {t('about.offer.point1')}
                </p>
              </div>
              
              <div className="text-center">
                <span className="text-sepia text-lg block mb-2">👉</span>
                <p className="font-cormorant text-lg text-cream/80">
                  {t('about.offer.point2')}
                </p>
              </div>
            </div>
            
            {/* Desktop version - inline layout */}
            <div className="hidden md:block space-y-4 max-w-lg mx-auto">
              <div className="flex items-start gap-3">
                <span className="text-sepia text-lg mt-0.5">👉</span>
                <p className="font-cormorant text-lg text-cream/80">
                  {t('about.offer.point1')}
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="text-sepia text-lg mt-0.5">👉</span>
                <p className="font-cormorant text-lg text-cream/80">
                  {t('about.offer.point2')}
                </p>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              {/* Mobile version */}
              <p className="md:hidden font-playfair text-base text-sepia-light tracking-wider whitespace-pre-line">
                {t('about.offer.footerMobile')}
              </p>
              {/* Desktop version */}
              <p className="hidden md:block font-playfair text-base text-sepia-light tracking-wider whitespace-pre-line">
                {t('about.offer.footerDesktop')}
              </p>
              {/* Disclaimer line - regular color (Mobile: 3 lines) */}
              <p className="md:hidden mt-6 font-cormorant text-lg text-cream/80 italic">
                La consultation ne vous sera facturée<br />que si vous choisissez de poursuivre<br />après les 10 minutes offertes.
              </p>
              {/* Disclaimer line - regular color (Desktop/Tablet: 2 lines) */}
              <p className="hidden md:block mt-6 font-cormorant text-lg text-cream/80 italic whitespace-pre-line">
                {t('about.offer.disclaimer')}
              </p>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 reveal">
          {[
            { icon: User, title: t('about.features.humans'), desc: t('about.features.humansDesc'), isLogo: false, isCounter: false },
            { icon: PawPrint, title: t('about.features.animals'), desc: t('about.features.animalsDesc'), isLogo: false, isCounter: false },
            { icon: null, title: 'years', desc: t('about.features.yearsDesc'), isLogo: true, isCounter: true }
          ].map((item, i) => (
            <div key={i} className="text-center p-6" ref={item.isCounter ? yearsRef : null}>
              <div className="w-16 h-16 mx-auto mb-4 border border-gold-muted/50 rounded-full flex items-center justify-center bg-dark-bg">
                {item.isLogo ? (
                  <span className="font-playfair text-xl text-sepia font-semibold tracking-wide">BM</span>
                ) : (
                  <item.icon size={24} className="text-sepia" strokeWidth={1.5} />
                )}
              </div>
              <h3 className="font-playfair text-xl md:text-lg text-cream mb-2">
                {item.isCounter ? (
                  <span className="text-sepia text-3xl font-bold">{yearsCount}</span>
                ) : item.title}
                {item.isCounter && <span className="text-cream"> {t('about.features.years')}</span>}
              </h3>
              <p className="font-cormorant text-lg md:text-base text-cream/90 whitespace-pre-line md:whitespace-normal">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Consultations Counter Badge */}
        <div className="mt-12 reveal" ref={consultationsRef}>
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-3 px-6 py-4 bg-dark-card border border-gold-muted/30 rounded-full">
              <Award size={24} className="text-sepia" />
              <span className="font-playfair text-lg text-cream">
                {t('about.consultationsPrefix')} <span className="text-sepia font-bold text-2xl">{consultationsCount.toLocaleString('fr-FR')}</span> {t('about.consultations')}
              </span>
              <CheckCircle size={20} className="text-green-500" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Service Card Component
const ServiceCard = ({ icon: Icon, title, description, price, note }) => (
  <div 
    className="service-card bg-dark-card p-8 shadow-xl relative border border-gold-muted/20 group"
    data-testid={`service-card-${title.toLowerCase().replace(' ', '-')}`}
  >
    <div className="absolute top-0 left-0 w-1 h-full bg-sepia opacity-0 group-hover:opacity-100 transition-opacity" />
    
    <div className="flex items-center gap-4 mb-6">
      <div className="service-icon w-14 h-14 rounded-full border border-gold-muted/50 flex items-center justify-center bg-dark-bg">
        <Icon size={28} className="text-sepia" strokeWidth={1.5} />
      </div>
      <h3 className="font-playfair text-xl text-cream">{title}</h3>
    </div>
    
    <p className="font-cormorant text-cream/90 leading-relaxed mb-6">
      {description}
    </p>
    
    <div className="font-playfair text-3xl text-sepia mb-2">{price}</div>
    <p className="font-cormorant text-sm text-sepia-light/70 italic">{note}</p>
  </div>
);

// Services Section
const ServicesSection = () => {
  const { t } = useLanguage();
  
  const services = [
    {
      icon: User,
      title: t('services.humanConsultation.title'),
      description: t('services.humanConsultation.description'),
      price: t('services.humanConsultation.price'),
      note: t('services.humanConsultation.note')
    },
    {
      icon: PawPrint,
      title: t('services.animalCommunication.title'),
      description: t('services.animalCommunication.description'),
      price: t('services.animalCommunication.price'),
      note: t('services.animalCommunication.note')
    }
  ];

  return (
    <section 
      id="services" 
      className="py-24 bg-dark-bg"
      data-testid="services-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-16 reveal">
          <p className="font-lato text-sm md:text-lg tracking-[0.3em] text-sepia-light uppercase mb-4 animate-blink">
            {t('services.badge')}
          </p>
          {/* Mobile title */}
          <h2 className="md:hidden font-playfair text-3xl text-cream mb-4">
            Comment puis-je<br />vous aider ?
          </h2>
          {/* Desktop/Tablet title */}
          <h2 className="hidden md:block font-playfair text-4xl text-cream mb-4">
            {t('services.title')}
          </h2>
          {/* Mobile subtitle - enlarged with break */}
          <p className="md:hidden font-cormorant text-xl text-cream/80 italic mb-4">
            Choisissez ce qui résonne<br />avec votre besoin
          </p>
          {/* Desktop/Tablet subtitle */}
          <p className="hidden md:block font-cormorant text-lg text-cream/80 italic mb-4">
            {t('services.subtitle')}
          </p>
          {/* Decorative Line */}
          <div className="flex justify-center">
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-sepia to-transparent"></div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 reveal">
          {services.map((service, i) => (
            <ServiceCard key={i} {...service} />
          ))}
        </div>

        {/* Trust Badges */}
        <TrustBadges />
      </div>
    </section>
  );
};

// Confidence Banner - Une consultation en toute confiance
const ConfidenceBanner = () => {
  return (
    <section className="py-16 bg-dark-bg">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="reveal">
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-dark-card to-dark-bg border border-gold-muted/40 p-8 md:p-10 relative overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-sepia/20 hover:border-sepia/60 group cursor-pointer">
            {/* Decorative corner elements */}
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-sepia/50 transition-all duration-300 group-hover:w-20 group-hover:h-20 group-hover:border-sepia"></div>
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-sepia/50 transition-all duration-300 group-hover:w-20 group-hover:h-20 group-hover:border-sepia"></div>
            
            {/* BM Logo */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto border-2 border-sepia rounded-full flex items-center justify-center bg-dark-bg transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-sepia/30">
                <span className="font-playfair text-2xl text-sepia font-semibold tracking-wide">BM</span>
              </div>
              {/* Mobile version */}
              <h3 className="md:hidden font-playfair text-2xl text-sepia mt-4 tracking-wide">
                Une consultation<br />en toute confiance
              </h3>
              {/* Desktop version */}
              <h3 className="hidden md:block font-playfair text-3xl text-sepia mt-4 tracking-wide">
                Une consultation en toute confiance
              </h3>
            </div>
            
            {/* Mobile version */}
            <p className="md:hidden font-cormorant text-xl text-cream/90 text-center italic mb-6">
              Chez BRICEMEDIUM.FR,<br />la liberté de choisir vous appartient.
            </p>
            {/* Desktop version */}
            <p className="hidden md:block font-cormorant text-xl text-cream/90 text-center italic mb-6">
              Chez BRICEMEDIUM.FR, la liberté de choisir vous appartient.
            </p>
            
            {/* Mobile version */}
            <p className="md:hidden font-cormorant text-lg text-cream/80 text-center mb-8">
              Les 10 premières minutes<br />de votre consultation<br />vous sont entièrement offertes.
            </p>
            {/* Desktop version */}
            <p className="hidden md:block font-cormorant text-lg text-cream/80 text-center mb-8">
              Les 10 premières minutes de votre consultation vous sont entièrement offertes.
            </p>
            
            {/* Mobile version - stacked layout */}
            <div className="md:hidden space-y-4 max-w-xl mx-auto mb-6">
              <div className="text-center">
                <span className="text-sepia text-lg block mb-2">👉</span>
                <p className="font-cormorant text-xl text-cream/80">
                  Ces premières minutes sont essentielles<br />pour découvrir Brice Médium,<br />ressentir son approche,<br />et apprécier pleinement<br />ses dons naturels.
                </p>
              </div>
            </div>
            
            {/* Desktop version - inline layout */}
            <div className="hidden md:block space-y-4 max-w-xl mx-auto mb-6">
              <div className="flex items-start gap-3">
                <span className="text-sepia text-lg mt-0.5">👉</span>
                <p className="font-cormorant text-xl text-cream/80">
                  Ces premières minutes sont essentielles pour découvrir Brice<br />Médium, ressentir son approche, et apprécier pleinement<br />ses dons naturels.
                </p>
              </div>
            </div>
            
            <div className="space-y-4 max-w-xl mx-auto">
              {/* Mobile version */}
              <p className="md:hidden font-cormorant text-lg text-cream/80 text-center">
                Et quelles que soient vos raisons,<br />si vous choisissez d'interrompre<br />la consultation<br />au terme des dix minutes offertes,<br /><strong className="text-cream">vous ne réglez absolument rien</strong>.
              </p>
              {/* Desktop version */}
              <p className="hidden md:block font-cormorant text-lg text-cream/80 text-center">
                Et quelles que soient vos raisons, si vous choisissez d'interrompre<br />la consultation au terme des dix minutes offertes,<br /><strong className="text-cream">vous ne réglez absolument rien</strong>.
              </p>
            </div>
            
            <div className="mt-8 text-center">
              <p className="font-playfair text-base text-sepia-light tracking-wider mb-4">
                Aucune pression.<br />Aucun piège.<br />Aucun engagement.
              </p>
              {/* Mobile version */}
              <p className="md:hidden font-cormorant text-xl text-cream/90 italic">
                Une relation fondée sur la confiance,<br />la sincérité,<br />et la transparence.
              </p>
              {/* Desktop version */}
              <p className="hidden md:block font-cormorant text-lg text-cream/90 italic">
                Une relation fondée sur la confiance, la sincérité et la transparence.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Gallery Section (empty - photos removed)
const GallerySection = () => {
  return (
    <section 
      id="gallery" 
      className="py-2 bg-dark-secondary"
      data-testid="gallery-section"
    >
    </section>
  );
};

// Process Section (Timeline)
const ProcessSection = () => {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const { t } = useLanguage();

  const steps = [
    { number: 1, title: t('process.steps.step1.title'), desc: t('process.steps.step1.description') },
    { number: 2, title: t('process.steps.step2.title'), desc: t('process.steps.step2.description') },
    { number: 3, title: t('process.steps.step3.title'), desc: 'special', isSpecial: true },
    { number: 4, title: t('process.steps.step4.title'), desc: t('process.steps.step4.description') }
  ];

  return (
    <>
      <section 
        id="process" 
        className="py-24 bg-dark-bg"
        data-testid="process-section"
      >
        <div className="max-w-4xl mx-auto px-2 sm:px-6">
          {/* Section Header */}
          <div className="text-center mb-16 reveal">
            <p className="font-lato text-base md:text-lg tracking-[0.3em] text-sepia-light uppercase mb-4 animate-blink">
              {t('process.badge')}
            </p>
            <h2 className="font-playfair text-3xl md:text-4xl text-cream mb-4">
              {t('process.title')}
            </h2>
            {/* Decorative Line */}
            <div className="flex justify-center">
              <div className="w-32 h-px bg-gradient-to-r from-transparent via-sepia to-transparent"></div>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative reveal">
            {/* Vertical Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gold-muted/50 -translate-x-1/2 hidden md:block" />
            <div className="absolute left-[18px] top-0 bottom-0 w-px bg-gold-muted/50 md:hidden" />

            <div className="space-y-16">
              {steps.map((step, i) => (
                <div 
                  key={i} 
                  className={`flex flex-col md:flex-row ${i % 2 === 1 ? 'md:flex-row-reverse' : ''} items-start md:items-center relative`}
                  data-testid={`process-step-${i + 1}`}
                >
                  {/* Number Circle */}
                  <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 w-10 h-10 md:w-12 md:h-12 bg-sepia text-dark-bg rounded-full flex items-center justify-center font-playfair text-base md:text-lg z-10">
                    {step.number}
                  </div>
                  
                  {/* Content */}
                  <div className={`w-[calc(100%-3.5rem)] md:w-[45%] bg-dark-card p-4 md:p-6 shadow-xl border border-gold-muted/20 ml-12 md:ml-0 ${i % 2 === 0 ? 'md:mr-auto md:text-right' : 'md:ml-auto'}`}>
                    <h4 className="font-playfair text-xl text-cream mb-2">{step.title}</h4>
                    {step.isSpecial ? (
                      <p className="font-cormorant text-lg md:text-xl text-cream/90">
                        {t('process.steps.step3.description')}{' '}
                        <button 
                          onClick={() => setShowInfoModal(true)}
                          className="text-sepia hover:text-gold-soft underline underline-offset-2 transition-colors"
                          data-testid="info-modal-trigger"
                        >
                          {t('process.steps.step3.link')}
                        </button>
                      </p>
                    ) : (
                      <p className="font-cormorant text-lg md:text-xl text-cream/90">{step.desc}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Info Modal */}
      <Dialog open={showInfoModal} onOpenChange={setShowInfoModal}>
        <DialogContent className="bg-dark-card border border-gold-muted/30 shadow-2xl max-w-md rounded-none modal-animate-in" data-testid="info-modal">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl text-cream text-center">
              {t('process.infoModal.title')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <p className="font-cormorant text-lg text-cream/90 text-center leading-relaxed">
              {t('process.infoModal.content')}
            </p>
          </div>
          <Button 
            onClick={() => setShowInfoModal(false)}
            className="w-full bg-sepia text-dark-bg hover:bg-gold-soft font-playfair tracking-wider py-4 rounded-none"
            data-testid="info-modal-close-btn"
          >
            {t('process.infoModal.button')}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const { t, language } = useLanguage();
  
  const testimonials = t('testimonials.items');
  
  // Map type keys to translated labels
  const getTypeLabel = (type) => {
    const typeMap = {
      human: t('testimonials.types.human'),
      animal: t('testimonials.types.animal'),
      family: t('testimonials.types.family')
    };
    return typeMap[type] || type;
  };

  // Double the testimonials for seamless infinite scroll
  const doubledTestimonials = [...testimonials, ...testimonials];

  return (
    <section 
      id="testimonials"
      className="py-24 bg-dark-secondary overflow-hidden"
      data-testid="testimonials-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-16 reveal">
          <p className="font-lato text-base md:text-base tracking-[0.3em] text-sepia-light uppercase mb-4 animate-blink">
            {t('testimonials.badge')}
          </p>
          <h2 className="font-playfair text-3xl md:text-4xl text-cream mb-4 whitespace-pre-line">
            {t('testimonials.title')}
          </h2>
          {/* Decorative Line */}
          <div className="flex justify-center">
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-sepia to-transparent"></div>
          </div>
        </div>
      </div>

      {/* Testimonials Horizontal Scroll */}
      <div className="testimonials-container w-full overflow-hidden">
        <div className="flex animate-scroll-left" style={{ width: 'fit-content' }}>
          {doubledTestimonials.map((testimonial, i) => (
            <div 
              key={i}
              className="flex-shrink-0 w-80 md:w-96 mx-4 bg-dark-card p-8 shadow-xl relative border border-gold-muted/20"
              data-testid={`testimonial-card-${i}`}
            >
              <span className="absolute top-5 left-5 font-playfair text-6xl text-gold-muted/20 leading-none">
                "
              </span>
              
              <p className="font-cormorant text-lg text-cream/90 leading-relaxed mb-6 italic pt-8">
                {testimonial.text}
              </p>
              
              <div>
                <p className="font-playfair text-sepia">{testimonial.author}</p>
                <p className="font-cormorant text-sm text-sepia-light/70 mt-1">{getTypeLabel(testimonial.type)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Trust Badges Component
const TrustBadges = ({ size = "normal" }) => {
  const { t } = useLanguage();
  const isLarge = size === "large";
  return (
    <div className={`flex flex-wrap justify-center items-center ${isLarge ? 'gap-8 md:gap-12' : 'gap-4 md:gap-6'} ${isLarge ? 'mt-0' : 'mt-8'} trust-badges`}>
      <div className="flex items-center gap-3 text-cream/70 trust-badge-item">
        <Lock size={isLarge ? 28 : 18} className="text-sepia" />
        <span className={`font-cormorant ${isLarge ? 'text-lg md:text-xl' : 'text-base md:text-lg'}`}>{t('trustBadges.securePayment')}</span>
      </div>
      <div className="flex items-center gap-3 text-cream/70 trust-badge-item">
        <Shield size={isLarge ? 28 : 18} className="text-sepia" />
        <span className={`font-cormorant ${isLarge ? 'text-lg md:text-xl' : 'text-base md:text-lg'}`}>{t('trustBadges.confidentiality')}</span>
      </div>
      <div className="flex items-center gap-3 text-cream/70 trust-badge-item">
        <CheckCircle size={isLarge ? 28 : 18} className="text-green-500" />
        <span className={`font-cormorant ${isLarge ? 'text-lg md:text-xl' : 'text-base md:text-lg'}`}>{t('trustBadges.satisfaction')}</span>
      </div>
    </div>
  );
};

// Try Banner - Et si vous essayiez…
const TryBanner = () => {
  return (
    <section className="py-16 bg-dark-secondary">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="reveal">
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-dark-card to-dark-bg border border-gold-muted/40 p-8 md:p-10 relative overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-sepia/20 hover:border-sepia/60 group cursor-pointer">
            {/* Decorative corner elements */}
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-sepia/50 transition-all duration-300 group-hover:w-20 group-hover:h-20 group-hover:border-sepia"></div>
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-sepia/50 transition-all duration-300 group-hover:w-20 group-hover:h-20 group-hover:border-sepia"></div>
            
            {/* BM Logo */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto border-2 border-sepia rounded-full flex items-center justify-center bg-dark-bg transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-sepia/30">
                <span className="font-playfair text-2xl text-sepia font-semibold tracking-wide">BM</span>
              </div>
              <h3 className="font-playfair text-2xl md:text-3xl text-sepia mt-4 tracking-wide">
                Et si vous essayiez…
              </h3>
            </div>
            
            {/* Mobile version */}
            <p className="md:hidden font-cormorant text-xl text-cream/90 text-center italic mb-8">
              Vous bénéficiez de 10 minutes offertes<br />pour vous faire votre propre ressenti.
            </p>
            {/* Desktop version */}
            <p className="hidden md:block font-cormorant text-xl text-cream/90 text-center italic mb-8">
              Vous bénéficiez de 10 minutes offertes pour vous faire votre propre ressenti.
            </p>
            
            {/* Mobile version - stacked layout */}
            <div className="md:hidden space-y-4 max-w-xl mx-auto">
              <div className="text-center">
                <span className="text-sepia text-lg block mb-2">👉</span>
                <p className="font-cormorant text-lg text-cream/80">
                  Si cela ne vous convient pas,<br />vous arrêtez… et <strong className="text-cream">vous ne payez rien</strong>.
                </p>
              </div>
              
              <div className="text-center">
                <span className="text-sepia text-lg block mb-2">👉</span>
                <p className="font-cormorant text-lg text-cream/80">
                  Si vous ressentez que la consultation<br />vous apporte quelque chose,<br />vous pouvez <strong className="text-cream">continuer librement</strong>.
                </p>
              </div>
            </div>
            
            {/* Desktop version - inline layout */}
            <div className="hidden md:block space-y-4 max-w-xl mx-auto">
              <div className="flex items-start gap-3">
                <span className="text-sepia text-lg mt-0.5">👉</span>
                <p className="font-cormorant text-lg text-cream/80">
                  Si cela ne vous convient pas, vous arrêtez… et <strong className="text-cream">vous ne payez rien</strong>.
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="text-sepia text-lg mt-0.5">👉</span>
                <p className="font-cormorant text-lg text-cream/80">
                  Si vous ressentez que la consultation vous apporte quelque chose,<br />vous pouvez <strong className="text-cream">continuer librement</strong>.
                </p>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              {/* Mobile version */}
              <p className="md:hidden font-playfair text-base text-sepia-light tracking-wider">
                Vous gardez le contrôle,<br />du début à la fin.
              </p>
              {/* Desktop version */}
              <p className="hidden md:block font-playfair text-base text-sepia-light tracking-wider">
                Vous gardez le contrôle,<br />du début à la fin.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ onOpenModal }) => {
  const { t } = useLanguage();
  
  return (
    <section 
      className="py-24 md:py-32 bg-gradient-to-b from-dark-bg to-dark-secondary relative text-center cta-section"
      data-testid="cta-section"
    >
      <div className="max-w-xl mx-auto px-4 sm:px-6 reveal">
        <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-cream mb-4 cta-title whitespace-pre-line">
          {t('cta.title')}
        </h2>
        {/* Decorative Line */}
        <div className="flex justify-center mb-6">
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-sepia to-transparent"></div>
        </div>
        <p className="font-cormorant text-xl md:text-lg text-cream/90 leading-relaxed mb-10 cta-text">
          {t('cta.subtitle')}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            onClick={onOpenModal}
            className="bg-sepia text-dark-bg hover:bg-gold-soft font-playfair tracking-wider px-10 py-6 rounded-none transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-sepia/20"
            data-testid="cta-primary-btn"
          >
            {t('cta.button')}
          </Button>
        </div>
      </div>
    </section>
  );
};

// Contact Form Component (for footer)
const ContactForm = () => {
  const [formData, setFormData] = useState({
    prenom: '',
    email: '',
    telephone: '',
    departement: '',
    ville: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(''); // 'success', 'error', ''
  const [statusMessage, setStatusMessage] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.prenom || !formData.email || !formData.departement || !formData.ville || !formData.message) {
      setStatus('error');
      setStatusMessage('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    setIsSubmitting(true);
    setStatus('');
    setStatusMessage('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setStatusMessage(data.message);
        setFormData({
          prenom: '',
          email: '',
          telephone: '',
          departement: '',
          ville: '',
          message: ''
        });
      } else {
        setStatus('error');
        setStatusMessage(data.detail || 'Une erreur est survenue.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus('error');
      setStatusMessage('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full bg-dark-secondary border border-gold-muted/30 text-cream px-3 py-2 text-sm focus:border-sepia outline-none font-cormorant placeholder:text-cream";

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          name="prenom"
          value={formData.prenom}
          onChange={handleChange}
          placeholder="Prénom *"
          disabled={isSubmitting}
          className={inputClass}
        />
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Email *"
          disabled={isSubmitting}
          className={inputClass}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="tel"
          name="telephone"
          value={formData.telephone}
          onChange={handleChange}
          placeholder="Téléphone"
          disabled={isSubmitting}
          className={inputClass}
        />
        <input
          type="text"
          name="departement"
          value={formData.departement}
          onChange={handleChange}
          placeholder="Département *"
          disabled={isSubmitting}
          className={inputClass}
        />
      </div>
      <input
        type="text"
        name="ville"
        value={formData.ville}
        onChange={handleChange}
        placeholder="Ville *"
        disabled={isSubmitting}
        className={inputClass}
      />
      <textarea
        name="message"
        value={formData.message}
        onChange={handleChange}
        placeholder="Votre message *"
        rows={3}
        disabled={isSubmitting}
        className={`${inputClass} resize-none`}
      />
      
      {status && (
        <div className={`p-2 text-xs font-cormorant rounded ${
          status === 'success' 
            ? 'bg-green-900/30 border border-green-500/50 text-green-300' 
            : 'bg-red-900/30 border border-red-500/50 text-red-300'
        }`}>
          {statusMessage}
        </div>
      )}
      
      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full py-2 font-playfair text-sm tracking-wider transition-all ${
          isSubmitting
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
            : 'bg-sepia text-dark-bg hover:bg-gold-soft'
        }`}
      >
        {isSubmitting ? 'Envoi...' : 'Envoyer'}
      </button>
    </form>
  );
};

// Contact Form Modal - opens from the footer "Formulaire de contact" link
const ContactFormModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="bg-dark-card border border-gold-muted/30 shadow-2xl max-w-md rounded-none modal-animate-in max-h-[90vh] overflow-y-auto"
        data-testid="contact-form-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-sepia text-center">
            Formulaire de contact
          </DialogTitle>
          <DialogDescription className="font-cormorant text-cream/80 text-center italic text-base">
            Une question ? Une demande ? Écrivez-moi.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4">
          <ContactForm />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Footer Section
const Footer = ({ onOpenCGV, onOpenCGU, onOpenMentions, onOpenFAQ, onOpenConditionsValidation, onOpenConfidentialite }) => {
  const { t } = useLanguage();
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);

  return (
    <footer 
      className="bg-dark-bg pt-16 pb-8 relative"
      data-testid="footer"
    >
      {/* Gold gradient line at top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sepia to-transparent" />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6">

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div className="text-center md:text-left">
            <h3 className="font-playfair text-2xl text-sepia mb-4">{t('preloader.title')}</h3>
            <p className="font-cormorant text-lg md:text-base text-cream/90 leading-relaxed footer-brand-text">
              {t('footer.slogan')}
            </p>
          </div>

          {/* Navigation */}
          <div className="text-center md:text-left">
            <h4 className="font-lato text-base tracking-[0.2em] uppercase text-gold-muted mb-6">{t('footer.navigation')}</h4>
            <ul className="space-y-3">
              {[
                { label: t('nav.monHistoire'), href: '/jesuis' },
                { label: t('nav.deroulement'), href: '/deroulement' },
                { label: t('nav.services'), href: '/services' },
                { label: t('nav.temoignages'), href: '/temoignages' },
                { label: t('nav.auxEtresChers'), href: '/hommages' },
                { label: t('nav.blog'), href: '/blog' }
              ].map((item, i) => (
                <li key={i}>
                  {item.href.startsWith('#') ? (
                    <a 
                      href={item.href}
                      className="font-cormorant text-sepia hover:text-gold-soft transition-colors footer-nav-link"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className="font-cormorant text-sepia hover:text-gold-soft transition-colors footer-nav-link"
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
              <li>
                <button 
                  onClick={onOpenFAQ}
                  className="font-cormorant text-sepia hover:text-gold-soft transition-colors footer-nav-link"
                >
                  {t('nav.faq')}
                </button>
              </li>
              <li>
                <Link
                  to="/partenaires"
                  className="font-cormorant text-sepia hover:text-gold-soft transition-colors footer-nav-link"
                >
                  {t('nav.partenaires')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="text-center md:text-left">
            <h4 className="font-lato text-base tracking-[0.2em] uppercase text-gold-muted mb-6">{t('footer.contact')}</h4>
            <ul className="space-y-3">
              <li>
                <a href="mailto:contact@bricemedium.fr" className="font-cormorant text-cream hover:text-sepia transition-colors flex items-center justify-center md:justify-start gap-2 footer-contact-link">
                  <Mail size={16} />
                  contact@bricemedium.fr
                </a>
              </li>
              <li>
                <a href="tel:+33760996931" className="font-cormorant text-cream hover:text-sepia transition-colors flex items-center justify-center md:justify-start gap-2 footer-contact-link">
                  <Phone size={16} />
                  07 60 99 69 31
                </a>
              </li>
              <li className="font-cormorant text-cream flex items-center justify-center md:justify-start gap-2 footer-contact-text">
                <MapPin size={16} />
                France/Monaco/Suisse<br />Belgique/Luxembourg
              </li>
              <li className="pt-2">
                <button
                  type="button"
                  onClick={() => setIsContactFormOpen(true)}
                  className="font-cormorant text-sepia hover:text-cream transition-colors flex items-center justify-center md:justify-start gap-2 underline decoration-sepia/40 underline-offset-4 hover:decoration-cream w-full md:w-auto"
                  data-testid="open-contact-form-btn"
                >
                  <Mail size={16} />
                  Formulaire de contact
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust Badges - Large */}
        <div className="py-10 border-t border-gold-muted/10">
          <TrustBadges size="large" />
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-gold-muted/10 text-center footer-legal">
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-4">
            <button 
              onClick={onOpenMentions}
              className="font-cormorant text-sm text-cream/50 hover:text-sepia transition-colors footer-legal-link"
            >
              {t('footer.legal')}
            </button>
            <button 
              onClick={onOpenConfidentialite}
              className="font-cormorant text-sm text-cream/50 hover:text-sepia transition-colors footer-legal-link"
            >
              {t('footer.confidentialite')}
            </button>
            <button 
              onClick={onOpenCGU}
              className="font-cormorant text-sm text-cream/50 hover:text-sepia transition-colors footer-legal-link"
            >
              {t('footer.cgu')}
            </button>
            <button 
              onClick={onOpenCGV}
              className="font-cormorant text-sm text-cream/50 hover:text-sepia transition-colors footer-legal-link"
            >
              {t('footer.cgv')}
            </button>
            <button 
              onClick={onOpenConditionsValidation}
              className="font-cormorant text-sm text-cream/50 hover:text-sepia transition-colors footer-legal-link"
            >
              {t('footer.conditionsValidation')}
            </button>
          </div>
          {/* Mobile version - 2 lines */}
          <p className="md:hidden font-cormorant text-sm text-cream/50 footer-copyright">
            © {new Date().getFullYear()} {t('preloader.title')} - {t('preloader.subtitle')}.<br />{t('footer.copyright')}.
          </p>
          {/* Desktop/Tablet version - 1 line */}
          <p className="hidden md:block font-cormorant text-sm text-cream/50 footer-copyright">
            © {new Date().getFullYear()} {t('preloader.title')} - {t('preloader.subtitle')}. {t('footer.copyright')}.
          </p>
        </div>
      </div>

      {/* Contact form modal (opens from the "Formulaire de contact" link above) */}
      <ContactFormModal isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />
    </footer>
  );
};

// Booking Modal
const BookingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    phone: '',
    preferredDays: '',
    preferredTimes: '',
    consultationType: '',
    comment: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [photoBase64, setPhotoBase64] = useState(null);
  const [photoName, setPhotoName] = useState('');
  const [photoError, setPhotoError] = useState('');

  const handlePhotoChange = (e) => {
    setPhotoError('');
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setPhotoBase64(null);
      setPhotoName('');
      return;
    }
    // Validate type
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      setPhotoError('Format invalide. Utilisez JPEG, PNG, GIF ou WEBP.');
      e.target.value = '';
      setPhotoBase64(null);
      setPhotoName('');
      return;
    }
    // Validate size (5 MB)
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('Image trop volumineuse (maximum 5 Mo).');
      e.target.value = '';
      setPhotoBase64(null);
      setPhotoName('');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoBase64(reader.result); // data:image/...;base64,xxxx
      setPhotoName(file.name);
    };
    reader.onerror = () => setPhotoError("Impossible de lire l'image.");
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setPhotoBase64(null);
    setPhotoName('');
    setPhotoError('');
    const input = document.getElementById('booking-photo-input');
    if (input) input.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

      // Build a human-readable message that captures all the booking info
      const typeLabel = formData.consultationType === 'humain'
        ? 'Un être humain'
        : formData.consultationType === 'animal'
          ? 'Un animal'
          : 'Non précisé';

      const messageLines = [
        `🪦 Demande de consultation « Contacter un défunt »`,
        ``,
        `• Type : ${typeLabel}`,
        `• Jours de préférence : ${formData.preferredDays || 'Non précisé'}`,
        `• Horaires de préférence : ${formData.preferredTimes || 'Non précisé'}`,
        ``,
        formData.comment ? `💬 Commentaire :\n${formData.comment}` : `💬 Commentaire : aucun`,
        ``,
        photoBase64 ? `📎 Photo jointe : ${photoName}` : `📎 Photo jointe : aucune`,
        ``,
        `Conditions tarifaires acceptées : ✅ Oui (10 minutes offertes puis 800€ en cas de poursuite).`
      ];
      const message = messageLines.join('\n');

      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prenom: formData.firstName,
          email: formData.email,
          telephone: formData.phone,
          message,
          source: 'booking',
          consultation_type: formData.consultationType || null,
          preferred_days: formData.preferredDays || null,
          preferred_times: formData.preferredTimes || null,
          comment: formData.comment || null,
          photo_base64: photoBase64 || null,
          photo_filename: photoName || null
        })
      });

      const data = await response.json();

      if (response.ok && data.success !== false) {
        toast.success('Demande envoyée !', {
          description: 'Merci pour votre confiance. Je vous recontacte sous 24h pour fixer notre rendez-vous.'
        });
        setFormData({ firstName: '', email: '', phone: '', preferredDays: '', preferredTimes: '', consultationType: '', comment: '' });
        setAcceptedTerms(false);
        handleRemovePhoto();
        onClose();
      } else {
        throw new Error(data.detail || data.message || "Échec de l'envoi");
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      toast.error('Erreur', {
        description: error.message || 'Une erreur est survenue. Veuillez réessayer.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-dark-card border border-gold-muted/30 shadow-2xl max-w-md rounded-none modal-animate-in max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-cream text-center">
            Contacter un défunt
          </DialogTitle>
          <DialogDescription className="font-cormorant text-cream/90 text-center italic text-lg">
            10 premières minutes gratuites.<br />
            Sans engagement.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          <Input
            placeholder="Votre prénom *"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            required
            className="bg-dark-bg border-gold-muted/30 text-cream placeholder:text-cream placeholder:text-lg focus:border-sepia rounded-none font-cormorant text-lg py-6"
            data-testid="booking-firstname-input"
          />
          
          <Input
            type="email"
            placeholder="Votre email *"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            className="bg-dark-bg border-gold-muted/30 text-cream placeholder:text-cream placeholder:text-lg focus:border-sepia rounded-none font-cormorant text-lg py-6"
            data-testid="booking-email-input"
          />
          
          <Input
            type="tel"
            placeholder="Votre téléphone *"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            required
            className="bg-dark-bg border-gold-muted/30 text-cream placeholder:text-cream placeholder:text-lg focus:border-sepia rounded-none font-cormorant text-lg py-6"
            data-testid="booking-phone-input"
          />

          <Input
            placeholder="Vos jours de préférence *"
            value={formData.preferredDays}
            onChange={(e) => setFormData({ ...formData, preferredDays: e.target.value })}
            className="bg-dark-bg border-gold-muted/30 text-cream placeholder:text-cream placeholder:text-lg focus:border-sepia rounded-none font-cormorant text-lg py-6"
            data-testid="booking-days-input"
          />

          <Input
            placeholder="Vos horaires de préférence *"
            value={formData.preferredTimes}
            onChange={(e) => setFormData({ ...formData, preferredTimes: e.target.value })}
            className="bg-dark-bg border-gold-muted/30 text-cream placeholder:text-cream placeholder:text-lg focus:border-sepia rounded-none font-cormorant text-lg py-6"
            data-testid="booking-times-input"
          />
          
          <Select
            value={formData.consultationType}
            onValueChange={(value) => setFormData({ ...formData, consultationType: value })}
            required
          >
            <SelectTrigger 
              className="bg-dark-bg border-gold-muted/30 text-cream focus:border-sepia rounded-none font-cormorant text-lg py-6"
              data-testid="booking-type-select"
            >
              <SelectValue placeholder="Qui souhaitez-vous contacter ? *" className="text-cream" />
            </SelectTrigger>
            <SelectContent className="bg-dark-card border-gold-muted/30 rounded-none">
              <SelectItem value="humain" className="text-cream font-cormorant text-lg">
                Un être humain
              </SelectItem>
              <SelectItem value="animal" className="text-cream font-cormorant text-lg">
                Un animal
              </SelectItem>
            </SelectContent>
          </Select>

          {/* Optional photo upload */}
          <div className="mt-2">
            <label className="block font-cormorant text-cream/90 text-base mb-2">
              Joindre une photo du défunt * <span className="text-cream/50 text-sm">(max 5 Mo)</span>
            </label>
            <input
              id="booking-photo-input"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handlePhotoChange}
              className="hidden"
              data-testid="booking-photo-input"
            />
            {!photoBase64 ? (
              <label
                htmlFor="booking-photo-input"
                className="flex items-center justify-center gap-3 w-full px-4 py-4 bg-dark-bg border-2 border-dashed border-gold-muted/40 hover:border-sepia rounded-none cursor-pointer transition-colors group"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-cream/70 group-hover:text-sepia transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-cormorant text-cream group-hover:text-sepia transition-colors text-base">
                  Cliquez pour choisir une photo
                </span>
              </label>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-dark-bg border border-sepia/40 rounded-none">
                <img src={photoBase64} alt="Aperçu" className="w-16 h-16 object-cover border border-gold-muted/30 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-cormorant text-cream text-base truncate">{photoName}</p>
                  <p className="font-cormorant text-cream/50 text-sm">Photo prête à être envoyée</p>
                </div>
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="text-cream/70 hover:text-red-400 transition-colors flex-shrink-0 p-2"
                  aria-label="Retirer la photo"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {photoError && (
              <p className="mt-2 font-cormorant text-sm text-red-400">{photoError}</p>
            )}
          </div>

          {/* Optional comment */}
          <div className="mt-2">
            <label className="block font-cormorant text-cream/90 text-base mb-2">
              Laisser un commentaire <span className="text-cream/50 text-sm">(facultatif)</span>
            </label>
            <Textarea
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              placeholder="Partagez ce qui vous tient à cœur, une question, un message…"
              rows={5}
              maxLength={1000}
              className="bg-dark-bg border-gold-muted/30 text-cream placeholder:text-cream/60 placeholder:text-base focus:border-sepia rounded-none font-cormorant text-lg min-h-[120px] resize-y"
              data-testid="booking-comment-textarea"
            />
            <p className="mt-1 font-cormorant text-xs text-cream/40 text-right">
              {formData.comment.length}/1000
            </p>
          </div>

          {/* Terms Acceptance Checkbox */}
          <div className="mt-4 p-4 bg-dark-bg/50 border border-gold-muted/30">
            <label className="flex items-start gap-3 cursor-pointer">
              <input 
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-5 h-5 accent-sepia cursor-pointer flex-shrink-0"
                data-testid="terms-checkbox"
              />
              <span className="font-cormorant text-base text-cream/90 leading-relaxed italic">
                Je reconnais que les 10 premières minutes de la consultation sont offertes.<br /><br />
                Je comprends que je peux mettre fin à la consultation avant l'expiration de ces 10 minutes sans aucun frais, et qu'aucun paiement ne me sera demandé dans ce cas.<br />
                Brice Médium m'indiquera oralement : «&nbsp;Les 10 minutes sont atteintes. Désirez-vous poursuivre la consultation&nbsp;?&nbsp;»<br /><br />
                Je comprends qu'au-delà des 10 minutes offertes, toute poursuite de la consultation entraînera automatiquement un paiement de <strong className="text-sepia">800€</strong>.<br /><br />
                En poursuivant volontairement la consultation après l'annonce des 10 minutes, j'accepte expressément cette condition tarifaire et reconnais que cette décision constitue un engagement ferme et définitif, excluant toute contestation ou demande de remboursement.
              </span>
            </label>
          </div>

          <Button 
            type="submit"
            disabled={isSubmitting || !acceptedTerms}
            className={`w-full font-playfair tracking-wider text-lg py-6 rounded-none mt-6 transition-all ${
              acceptedTerms 
                ? 'bg-sepia text-dark-bg hover:bg-gold-soft' 
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="booking-submit-btn"
          >
            {isSubmitting ? 'Envoi en cours...' : 'Demander un rendez-vous'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// CGV Modal Component
const CGVModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-dark-card border border-gold-muted/30 shadow-2xl max-w-3xl rounded-none modal-animate-in max-h-[90vh] overflow-y-auto" data-testid="cgv-modal">
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-cream text-center">
            CONDITIONS GÉNÉRALES DE VENTE (CGV)
          </DialogTitle>
        </DialogHeader>

        <div className="py-6 font-cormorant text-cream/90 space-y-6 text-base leading-relaxed">
          
          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">1. Objet</h3>
            <p>Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre :</p>
            <p className="mt-2">La société <strong>LVEB GROUP</strong>,<br />
            ci-après dénommée "le Prestataire",<br />
            exploitant le site et la marque commerciale <strong>BRICEMEDIUM.FR</strong>,<br />
            et toute personne physique ou morale, ci-après dénommée "le Client",<br />
            dans le cadre de la vente de prestations de voyance et d'abonnements proposés sur le site BRICEMEDIUM.FR.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">2. Acceptation des CGV</h3>
            <p>Toute commande ou utilisation des services implique l'acceptation pleine, entière et sans réserve des présentes CGV.</p>
            <p className="mt-2">Le Client reconnaît en avoir pris connaissance avant tout paiement.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">3. Nature des services</h3>
            <p>Les services proposés incluent :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>consultations de voyance</li>
              <li>prestations personnalisées</li>
              <li>abonnements</li>
            </ul>
            <p className="mt-2">Ces services relèvent exclusivement du divertissement et du bien-être.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">4. Avertissement essentiel</h3>
            <p>Le Client reconnaît que :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>la voyance repose sur une interprétation subjective</li>
              <li>aucune garantie de résultat n'est fournie</li>
              <li>les prestations ne constituent pas un avis médical, juridique ou financier</li>
            </ul>
            <p className="mt-2">Toute décision prise relève de la responsabilité exclusive du Client.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">5. Prix</h3>
            <ul className="list-disc list-inside ml-4">
              <li>Consultation : <strong>800€ TTC</strong></li>
              <li>Abonnements : selon offre</li>
            </ul>
            <p className="mt-2">Les prix peuvent être modifiés à tout moment.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">6. Paiement</h3>
            <p>Le paiement est exigible immédiatement.</p>
            <p className="mt-2">Moyens acceptés :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>carte bancaire</li>
              <li>plateformes sécurisées (Stripe, PayPal…)</li>
            </ul>
            <p className="mt-2">Le Prestataire n'a jamais accès aux données bancaires.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">7. Abonnements</h3>
            <p><strong>7.1 Reconduction</strong><br />Les abonnements sont reconduits automatiquement.</p>
            <p className="mt-2"><strong>7.2 Résiliation</strong><br />Le Client peut résilier à tout moment.</p>
            <p className="mt-2"><strong>7.3 Non-remboursement</strong><br />Toute période entamée est due et non remboursable.</p>
          </section>

          <section className="bg-dark-bg/50 p-4 border border-gold-muted/30">
            <h3 className="font-playfair text-lg text-sepia mb-2">🔮 8. CONSULTATION AVEC 10 MINUTES OFFERTES</h3>
            
            <p><strong>8.1 Principe</strong><br />
            Le Prestataire propose une consultation incluant :<br />
            👉 <strong>10 minutes gratuites sans engagement</strong></p>
            <p className="mt-2">Pendant ces 10 minutes :</p>
            <ul className="list-disc list-inside ml-4">
              <li>le Client peut interrompre la consultation librement</li>
              <li>aucun paiement ne sera exigé</li>
            </ul>

            <p className="mt-4"><strong>8.2 Acceptation de poursuite</strong><br />
            Au-delà des 10 premières minutes :<br />
            👉 le Client accepte expressément de poursuivre la consultation</p>
            <p className="mt-2">Cette poursuite vaut :</p>
            <ul className="list-disc list-inside ml-4">
              <li>acceptation du tarif de 800€ TTC</li>
              <li>engagement ferme de paiement</li>
            </ul>

            <p className="mt-4"><strong>8.3 Exigibilité du paiement</strong><br />
            Dès dépassement des 10 minutes :</p>
            <ul className="list-disc list-inside ml-4">
              <li>la prestation devient immédiatement payable</li>
              <li>le montant de 800€ est dû dans son intégralité</li>
            </ul>

            <p className="mt-4"><strong>8.4 Refus de paiement</strong><br />
            En cas de refus de paiement après les 10 minutes :</p>
            <ul className="list-disc list-inside ml-4">
              <li>la consultation pourra être interrompue</li>
              <li>la somme de 800€ restera due</li>
              <li>des procédures de recouvrement pourront être engagées</li>
            </ul>

            <p className="mt-4"><strong>8.5 Preuve d'acceptation</strong><br />
            Le fait de poursuivre la consultation constitue une acceptation irrévocable.</p>
            <p className="mt-2">Cette acceptation pourra être prouvée par :</p>
            <ul className="list-disc list-inside ml-4">
              <li>durée de la consultation</li>
              <li>logs techniques</li>
              <li>historiques d'échange</li>
            </ul>

            <p className="mt-4"><strong>8.6 Retard et absence</strong></p>
            <ul className="list-disc list-inside ml-4">
              <li>Absence : aucun droit aux 10 minutes offertes</li>
              <li>Retard : durée réduite</li>
            </ul>

            <p className="mt-4"><strong>8.7 Annulation</strong><br />
            Annulation possible jusqu'à 24h/48h avant.<br />
            Passé ce délai : aucun remboursement.</p>

            <p className="mt-4"><strong>8.8 Force majeure</strong><br />
            En cas d'impossibilité :</p>
            <ul className="list-disc list-inside ml-4">
              <li>report proposé</li>
              <li>ou remboursement si non réalisée</li>
            </ul>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">9. Droit de rétractation</h3>
            <p>Le Client :</p>
            <ul className="list-disc list-inside ml-4">
              <li>demande l'exécution immédiate du service</li>
              <li>renonce expressément à son droit de rétractation</li>
            </ul>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">10. Remboursement</h3>
            <ul className="list-disc list-inside ml-4">
              <li>Aucun remboursement après dépassement des 10 minutes</li>
              <li>Aucun remboursement après prestation</li>
              <li>Aucun remboursement sur abonnement en cours</li>
            </ul>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">11. Responsabilité</h3>
            <p>Le Prestataire est soumis à une obligation de moyens.</p>
            <p className="mt-2">Il ne peut être tenu responsable :</p>
            <ul className="list-disc list-inside ml-4">
              <li>des décisions du Client</li>
              <li>des conséquences des consultations</li>
            </ul>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">12. Comportement du client</h3>
            <p>Tout comportement abusif entraîne :</p>
            <ul className="list-disc list-inside ml-4">
              <li>arrêt immédiat</li>
              <li>exclusion du service</li>
              <li>sans remboursement</li>
            </ul>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">13. Données personnelles</h3>
            <p>Traitement conforme RGPD.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">14. Propriété intellectuelle</h3>
            <p>Tous les contenus sont protégés.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">15. Litiges et médiation</h3>
            <p>Une solution amiable sera privilégiée.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">16. Droit applicable</h3>
            <p>Droit français.<br />
            Tribunaux compétents : ressort du siège de LVEB GROUP.</p>
          </section>

          <section className="bg-dark-bg/50 p-4 border border-gold-muted/30">
            <h3 className="font-playfair text-lg text-sepia mb-2">🔐 17. CLAUSE ANTI-CHARGEBACK</h3>
            
            <p><strong>17.1 Acceptation</strong><br />
            Le Client reconnaît :</p>
            <ul className="list-disc list-inside ml-4">
              <li>accepter les 10 minutes gratuites</li>
              <li>accepter le paiement de 800€ après ce délai</li>
              <li>renoncer à toute contestation abusive</li>
            </ul>

            <p className="mt-4"><strong>17.2 Preuve</strong><br />
            Font foi :</p>
            <ul className="list-disc list-inside ml-4">
              <li>durée de consultation</li>
              <li>adresse IP</li>
              <li>logs techniques</li>
              <li>emails</li>
            </ul>

            <p className="mt-4"><strong>17.3 Engagement</strong><br />
            Le Client s'engage à contacter le Prestataire avant toute procédure bancaire.</p>

            <p className="mt-4"><strong>17.4 Fraude</strong><br />
            Toute contestation abusive pourra entraîner :</p>
            <ul className="list-disc list-inside ml-4">
              <li>poursuites</li>
              <li>dommages et intérêts</li>
            </ul>

            <p className="mt-4"><strong>17.5 Clause pénale</strong><br />
            Indemnité forfaitaire minimale : <strong>150€</strong></p>

            <p className="mt-4"><strong>17.6 Reconnaissance du service</strong><br />
            Le Client reconnaît que :</p>
            <ul className="list-disc list-inside ml-4">
              <li>la prestation devient payante après 10 minutes</li>
              <li>le paiement correspond à un service rendu</li>
            </ul>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">18. Conservation des preuves</h3>
            <p>Les données peuvent être conservées à des fins de preuve.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">19. Modification des CGV</h3>
            <p>Les CGV peuvent être modifiées à tout moment.</p>
            <p className="mt-4 text-sepia italic">Dernière mise à jour : 31.03.2026</p>
          </section>

        </div>

        <Button 
          onClick={onClose}
          className="w-full bg-sepia text-dark-bg hover:bg-gold-soft font-playfair tracking-wider py-4 rounded-none"
        >
          Fermer
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// Mentions Légales Modal Component
const MentionsLegalesModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-dark-card border border-gold-muted/30 shadow-2xl max-w-3xl rounded-none modal-animate-in max-h-[90vh] overflow-y-auto" data-testid="mentions-modal">
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-cream text-center">
            MENTIONS LÉGALES
          </DialogTitle>
        </DialogHeader>

        <div className="py-6 font-cormorant text-cream/90 space-y-6 text-base leading-relaxed">
          
          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">1. Éditeur du site</h3>
            <p>Le présent site internet accessible à l'adresse <strong>BRICEMEDIUM.FR</strong> est édité par :</p>
            <div className="mt-3 bg-dark-bg/50 p-4 border border-gold-muted/20">
              <p><strong>LVEB GROUP</strong></p>
              <p>Forme juridique : SASU</p>
              <p>Capital social : 100 €</p>
              <p>Siège social : 50 Avenue des Champs-Elysées - 75008 Paris</p>
              <p>Numéro SIRET : 108641051</p>
              <p>Numéro RCS : 108641051 R.C.S Paris</p>
              <p>Numéro TVA intracommunautaire : FR7501.108641051</p>
            </div>
            <p className="mt-3">Le site BRICEMEDIUM.FR est exploité en tant que marque commerciale / nom de domaine par la société LVEB GROUP.</p>
            <p className="mt-2"><strong>Directeur de la publication :</strong></p>
            <div className="mt-3">
              <p><strong>Contact :</strong></p>
              <p>📧 Email : contact@bricemedium.fr</p>
            </div>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">2. Hébergement</h3>
            <p>Le site est hébergé par :</p>
            <div className="mt-3 bg-dark-bg/50 p-4 border border-gold-muted/20">
              <p><strong>LWS.FR</strong></p>
              <p>Adresse : 4 rue Galvani, 75017 Paris</p>
              <p>Téléphone : 01 77 62 3003</p>
              <p>Site web : <a href="https://www.lws.fr/nom-de-domaine.php" target="_blank" rel="noopener noreferrer" className="text-sepia hover:underline">https://www.lws.fr</a></p>
            </div>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">3. Accès au site</h3>
            <p>Le site est accessible 24h/24, 7j/7, sauf interruption pour maintenance ou cas de force majeure.</p>
            <p className="mt-2">L'éditeur ne peut être tenu responsable en cas d'indisponibilité temporaire du site.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">4. Description des services</h3>
            <p>Le site propose des services de voyance comprenant notamment :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>consultations personnalisées</li>
              <li>prestations ponctuelles</li>
              <li>abonnements</li>
            </ul>
            <p className="mt-2">Ces services sont fournis dans un cadre de divertissement et de bien-être.</p>
          </section>

          <section className="bg-dark-bg/50 p-4 border border-gold-muted/30">
            <h3 className="font-playfair text-lg text-sepia mb-2">⚠️ 5. Avertissement important</h3>
            <p>Les services proposés :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>ne reposent sur aucune base scientifique</li>
              <li>ne constituent pas un avis médical, juridique ou financier</li>
              <li>ne garantissent aucun résultat</li>
            </ul>
            <p className="mt-3"><strong>L'utilisateur reste seul responsable de ses décisions.</strong></p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">6. Propriété intellectuelle</h3>
            <p>L'ensemble des contenus du site (textes, images, logo, vidéos, éléments graphiques) est protégé par le droit de la propriété intellectuelle.</p>
            <p className="mt-2">Toute reproduction, modification ou diffusion sans autorisation écrite est interdite.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">7. Données personnelles</h3>
            <p>Les données personnelles sont collectées et traitées conformément au Règlement Général sur la Protection des Données (RGPD).</p>
            <p className="mt-3">L'utilisateur dispose des droits suivants :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>accès</li>
              <li>rectification</li>
              <li>suppression</li>
              <li>opposition</li>
              <li>portabilité</li>
            </ul>
            <p className="mt-3">Pour exercer ces droits : contact@bricemedium.fr</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">8. Cookies</h3>
            <p>Le site utilise des cookies pour :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>améliorer l'expérience utilisateur</li>
              <li>analyser le trafic</li>
              <li>proposer des contenus adaptés</li>
            </ul>
            <p className="mt-2">L'utilisateur peut gérer ses préférences via le bandeau cookies.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">9. Responsabilité</h3>
            <p>La société LVEB GROUP ne peut être tenue responsable :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>des erreurs ou omissions sur le site</li>
              <li>de l'utilisation des informations</li>
              <li>des conséquences liées aux consultations</li>
            </ul>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">10. Liens externes</h3>
            <p>Le site peut contenir des liens vers des sites tiers.</p>
            <p className="mt-2">LVEB GROUP n'exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">11. Droit applicable</h3>
            <p>Les présentes mentions légales sont soumises au droit français.</p>
            <p className="mt-2">Tout litige relève de la compétence des tribunaux du ressort du siège social de LVEB GROUP.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">12. Contact</h3>
            <p>Pour toute question :</p>
            <p className="mt-2">📧 contact@bricemedium.fr</p>
            <p className="mt-4 text-sepia italic">Dernière mise à jour : 31.03.2026</p>
          </section>

        </div>

        <Button 
          onClick={onClose}
          className="w-full bg-sepia text-dark-bg hover:bg-gold-soft font-playfair tracking-wider py-4 rounded-none"
        >
          Fermer
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// Confidentialité Modal Component
const ConfidentialiteModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-dark-card border border-gold-muted/30 shadow-2xl max-w-3xl rounded-none modal-animate-in max-h-[90vh] overflow-y-auto" data-testid="confidentialite-modal">
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-cream text-center">
            POLITIQUE DE CONFIDENTIALITÉ
          </DialogTitle>
        </DialogHeader>

        <div className="py-6 font-cormorant text-cream/90 space-y-6 text-base leading-relaxed">
          
          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">1. Préambule</h3>
            <p>La présente politique de confidentialité a pour objectif d'informer les utilisateurs du site <strong>BRICEMEDIUM.FR</strong>, exploité par la société <strong>LVEB GROUP</strong>, sur la manière dont leurs données personnelles sont collectées, utilisées et protégées.</p>
            <p className="mt-2">Elle complète les mentions légales, les CGU et les CGV.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">2. Données collectées</h3>
            <p>Dans le cadre de l'utilisation du site, les données suivantes peuvent être collectées :</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
              <li>Nom et prénom</li>
              <li>Adresse email</li>
              <li>Numéro de téléphone</li>
              <li>Informations de paiement (via prestataire sécurisé)</li>
              <li>Données de connexion (adresse IP, navigateur, etc.)</li>
              <li>Données liées aux rendez-vous et consultations</li>
            </ul>
            <p className="mt-4">Ces données sont collectées lorsque :</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
              <li>vous remplissez un formulaire</li>
              <li>vous prenez un rendez-vous</li>
              <li>vous effectuez un paiement</li>
              <li>vous naviguez sur le site</li>
            </ul>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">3. Finalités du traitement</h3>
            <p>Les données collectées sont utilisées pour :</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
              <li>la gestion des rendez-vous</li>
              <li>la réalisation des consultations</li>
              <li>la gestion des paiements (Stripe ou équivalent)</li>
              <li>le service client</li>
              <li>l'amélioration du site</li>
              <li>la prévention des fraudes et litiges</li>
            </ul>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">4. Base légale</h3>
            <p>Les traitements reposent sur :</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
              <li>le consentement de l'utilisateur</li>
              <li>l'exécution d'un contrat (consultation / abonnement)</li>
              <li>les obligations légales</li>
              <li>l'intérêt légitime du Prestataire</li>
            </ul>
            <p className="mt-4">Conformément au RGPD, les données doivent être traitées de manière licite, loyale et transparente.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">5. Destinataires des données</h3>
            <p>Les données peuvent être transmises à :</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
              <li>prestataires techniques (hébergement, outils web)</li>
              <li>solutions de paiement (Stripe, etc.)</li>
              <li>outils d'analyse (statistiques)</li>
            </ul>
            <p className="mt-4 font-semibold text-cream">Les données ne sont jamais vendues à des tiers.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">6. Durée de conservation</h3>
            <p>Les données sont conservées :</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
              <li>pendant la durée de la relation commerciale</li>
              <li>puis archivées à des fins légales (facturation, litiges)</li>
              <li>cookies : maximum 13 mois</li>
            </ul>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">7. Sécurité</h3>
            <p><strong>LVEB GROUP</strong> met en œuvre des mesures techniques et organisationnelles pour protéger les données :</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
              <li>connexions sécurisées</li>
              <li>accès limité</li>
              <li>protection contre les accès non autorisés</li>
            </ul>
            <p className="mt-4">Cependant, aucun système n'est totalement sécurisé.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">8. Cookies</h3>
            <p>Le site utilise des cookies pour :</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
              <li>assurer le bon fonctionnement du site</li>
              <li>mesurer l'audience</li>
              <li>améliorer l'expérience utilisateur</li>
            </ul>
            <p className="mt-4">L'utilisateur peut accepter, refuser ou paramétrer les cookies via le bandeau prévu à cet effet.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">9. Droits de l'utilisateur</h3>
            <p>Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
              <li>droit d'accès</li>
              <li>droit de rectification</li>
              <li>droit à l'effacement</li>
              <li>droit d'opposition</li>
              <li>droit à la portabilité</li>
              <li>droit à la limitation</li>
            </ul>
            <p className="mt-4">Vous pouvez exercer ces droits à tout moment.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">10. Contact</h3>
            <p>Pour toute demande relative à vos données :</p>
            <p className="mt-2"><strong className="text-sepia">contact@bricemedium.fr</strong></p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">11. Autorité de contrôle</h3>
            <p>En cas de litige, vous pouvez saisir la <strong>CNIL</strong> (Commission Nationale de l'Informatique et des Libertés).</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">12. Modification</h3>
            <p>La présente politique peut être modifiée à tout moment afin de rester conforme à la réglementation en vigueur.</p>
            <p className="mt-2">La version applicable est celle publiée sur le site.</p>
          </section>

          <div className="pt-4 border-t border-gold-muted/30 text-center">
            <p className="text-cream/50 text-sm">Dernière mise à jour : 05.04.2026</p>
          </div>

        </div>

        <Button 
          onClick={onClose}
          className="w-full bg-sepia text-dark-bg hover:bg-gold-soft font-playfair tracking-wider py-4 rounded-none"
        >
          Fermer
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// CGU Modal Component
const CGUModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-dark-card border border-gold-muted/30 shadow-2xl max-w-3xl rounded-none modal-animate-in max-h-[90vh] overflow-y-auto" data-testid="cgu-modal">
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-cream text-center">
            CONDITIONS GÉNÉRALES D'UTILISATION (CGU)
          </DialogTitle>
        </DialogHeader>

        <div className="py-6 font-cormorant text-cream/90 space-y-6 text-base leading-relaxed">
          
          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">1. Objet</h3>
            <p>Les présentes Conditions Générales d'Utilisation (CGU) ont pour objet de définir les modalités d'accès et d'utilisation du site <strong>BRICEMEDIUM.FR</strong>, exploité par la société <strong>LVEB GROUP</strong>.</p>
            <p className="mt-2">Toute navigation sur le site implique l'acceptation pleine et entière des présentes CGU.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">2. Accès au site</h3>
            <p>Le site est accessible gratuitement à tout utilisateur disposant d'un accès à Internet.</p>
            <p className="mt-2">Tous les frais liés à l'accès (matériel, connexion, etc.) restent à la charge de l'utilisateur.</p>
            <p className="mt-2">Le site est accessible 24h/24 et 7j/7, sauf interruption pour maintenance ou cas de force majeure.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">3. Conditions d'accès</h3>
            <p>L'utilisation du site est réservée aux personnes :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>âgées de 18 ans minimum</li>
              <li>disposant de la capacité juridique</li>
            </ul>
            <p className="mt-2">L'utilisateur s'engage à fournir des informations exactes.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">4. Description des services</h3>
            <p>Le site propose :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>des services de voyance</li>
              <li>des consultations personnalisées</li>
              <li>des abonnements</li>
            </ul>
            <p className="mt-2">Les services sont fournis à titre de divertissement et de bien-être uniquement.</p>
          </section>

          <section className="bg-dark-bg/50 p-4 border border-gold-muted/30">
            <h3 className="font-playfair text-lg text-sepia mb-2">⚠️ 5. Avertissement</h3>
            <p>L'utilisateur reconnaît que :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>la voyance repose sur une interprétation subjective</li>
              <li>aucune garantie de résultat n'est fournie</li>
              <li>les services ne constituent pas un avis médical, juridique ou financier</li>
            </ul>
            <p className="mt-3"><strong>L'utilisateur reste seul responsable de ses décisions.</strong></p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">6. Comportement de l'utilisateur</h3>
            <p>L'utilisateur s'engage à :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>utiliser le site de manière légale</li>
              <li>ne pas porter atteinte au site ou au Prestataire</li>
              <li>ne pas adopter de comportement abusif, frauduleux ou nuisible</li>
            </ul>
            <p className="mt-2">Tout abus pourra entraîner la suspension ou suppression de l'accès.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">7. Accessibilité du site</h3>
            <p>La société LVEB GROUP s'efforce d'assurer un accès au site 24h/24 et 7j/7.</p>
            <p className="mt-2">Toutefois, l'accès peut être suspendu à tout moment pour maintenance, mise à jour ou cas de force majeure.</p>
            <p className="mt-2">Le Prestataire est soumis à une obligation de moyens.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">8. Difficultés ou impossibilité d'accès</h3>
            <p>LVEB GROUP ne pourra être tenue responsable :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>des interruptions du site</li>
              <li>des dysfonctionnements techniques</li>
              <li>des défaillances du réseau Internet</li>
              <li>des problèmes liés au matériel de l'utilisateur</li>
            </ul>
            <p className="mt-2">L'utilisateur reconnaît utiliser le site à ses propres risques.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">9. Force majeure</h3>
            <p>La responsabilité de LVEB GROUP ne pourra être engagée en cas de force majeure.</p>
            <p className="mt-2">Sont notamment considérés comme tels :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>catastrophes naturelles</li>
              <li>pannes techniques</li>
              <li>grèves</li>
              <li>défaillance des réseaux</li>
              <li>événements imprévisibles indépendants de la volonté du Prestataire</li>
            </ul>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">10. Responsabilité</h3>
            <p>LVEB GROUP ne peut être tenue responsable :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>des dommages liés à l'utilisation du site</li>
              <li>des décisions prises par l'utilisateur</li>
              <li>des conséquences des consultations</li>
            </ul>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">11. Propriété intellectuelle</h3>
            <p>Tous les éléments du site sont protégés.</p>
            <p className="mt-2">Toute reproduction ou utilisation sans autorisation est interdite.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">12. Données personnelles</h3>
            <p>Les données sont traitées conformément au RGPD.</p>
            <p className="mt-2">L'utilisateur dispose des droits suivants :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>accès</li>
              <li>rectification</li>
              <li>suppression</li>
              <li>opposition</li>
              <li>portabilité</li>
            </ul>
            <p className="mt-2">Contact : contact@bricemedium.fr</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">13. Cookies</h3>
            <p>Le site utilise des cookies pour :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>améliorer l'expérience utilisateur</li>
              <li>analyser le trafic</li>
            </ul>
            <p className="mt-2">Les cookies sont conservés pour une durée maximale de 13 mois.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">14. Sécurité</h3>
            <p>Le site met en œuvre des mesures de sécurité.</p>
            <p className="mt-2">Toutefois, l'utilisateur reconnaît que le risque zéro n'existe pas sur Internet.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">15. Liens externes</h3>
            <p>Le site peut contenir des liens vers des sites tiers.</p>
            <p className="mt-2">LVEB GROUP n'exerce aucun contrôle sur ces sites.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">16. Suspension / restriction d'accès</h3>
            <p>LVEB GROUP se réserve le droit de :</p>
            <ul className="list-disc list-inside ml-4 mt-2">
              <li>suspendre l'accès au site</li>
              <li>supprimer un compte</li>
              <li>refuser un utilisateur</li>
            </ul>
            <p className="mt-2">sans préavis en cas de non-respect des CGU.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">17. Modification des CGU</h3>
            <p>Les CGU peuvent être modifiées à tout moment.</p>
            <p className="mt-2">La version applicable est celle en ligne.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">18. Droit applicable</h3>
            <p>Les présentes CGU sont soumises au droit français.</p>
            <p className="mt-2">Tout litige relève de la compétence des tribunaux du ressort du siège de LVEB GROUP.</p>
          </section>

          <section>
            <h3 className="font-playfair text-lg text-sepia mb-2">19. Contact</h3>
            <p>📧 contact@bricemedium.fr</p>
            <p className="mt-4 text-sepia italic">Dernière mise à jour : 31.03.2026</p>
          </section>

        </div>

        <Button 
          onClick={onClose}
          className="w-full bg-sepia text-dark-bg hover:bg-gold-soft font-playfair tracking-wider py-4 rounded-none"
        >
          Fermer
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// FAQ Modal Component
const FAQModal = ({ isOpen, onClose }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqItems = [
    {
      question: "Comment se passe les 10 minutes gratuites ?",
      answer: "Dès le début de la séance, je me connecte à l'énergie. Si un esprit se manifeste avec des éléments vérifiables (prénom, relation, cause du décès, objets précis), vous savez immédiatement que la connexion est réelle. Vous ne payez que si vous reconnaissez votre défunt."
    },
    {
      question: "Et si aucun esprit ne se manifeste ?",
      answer: "Cela arrive rarement, mais si après 10 minutes aucun contact n'est établi, la séance s'arrête là et vous ne payez rien. L'honnêteté est essentielle dans mon travail. Je ne fabrique jamais de messages."
    },
    {
      question: "Les animaux communiquent-ils vraiment ?",
      answer: "Oui, absolument. Les animaux ont une conscience qui survit. Ils transmettent souvent des images, des sensations (chaleur, froid, douleur à l'endroit de leur décès) et parfois des mots simples. Ils veillent sur leurs humains avec autant d'amour que de leur vivant."
    },
    {
      question: "Puis-je poser des questions précises ?",
      answer: "Oui, après la connexion initiale. Cependant, je ne contrôle pas ce que l'esprit choisit de dire. Parfois ils répondent directement, parfois ils insistent sur d'autres messages plus importants pour eux. Je suis le messager, pas le directeur de la conversation."
    },
    {
      question: "Comment se fait le paiement ?",
      answer: "Après les 10 minutes gratuites, si vous souhaitez continuer, je vous envoie un lien de paiement sécurisé par carte bancaire. La consultation se poursuit immédiatement après validation."
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-dark-card border border-gold-muted/30 shadow-2xl max-w-2xl rounded-none modal-animate-in max-h-[90vh] overflow-y-auto" data-testid="faq-modal">
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-cream text-center">
            Questions Fréquentes
          </DialogTitle>
          <DialogDescription className="font-cormorant text-cream/80 text-center italic text-lg">
            Clarifications
          </DialogDescription>
        </DialogHeader>

        {/* Decorative Line */}
        <div className="flex justify-center my-4">
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-sepia to-transparent"></div>
        </div>

        <div className="py-4 space-y-3">
          {faqItems.map((item, i) => (
            <div 
              key={i} 
              className="border border-gold-muted/30 bg-dark-bg/50"
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full px-5 py-4 flex justify-between items-center text-left hover:bg-dark-secondary/50 transition-colors"
              >
                <span className="font-cormorant text-lg text-cream pr-4">{item.question}</span>
                <ChevronDown 
                  size={20} 
                  className={`text-sepia flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} 
                />
              </button>
              {openFaq === i && (
                <div className="px-5 pb-4">
                  <p className="font-cormorant text-cream/80 leading-relaxed">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <Button 
          onClick={onClose}
          className="w-full bg-sepia text-dark-bg hover:bg-gold-soft font-playfair tracking-wider py-4 rounded-none mt-4"
        >
          Fermer
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// Conditions de Validation Modal Component
const ConditionsValidationModal = ({ isOpen, onClose }) => {
  const [isChecked, setIsChecked] = useState(false);
  const [consultationType, setConsultationType] = useState(null); // 'personne' or 'animal'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Button is enabled only if both conditions are met
  const canProceed = isChecked && consultationType !== null;

  const handlePayment = async () => {
    if (!canProceed) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const originUrl = window.location.origin;
      
      const response = await fetch(`${BACKEND_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          origin_url: originUrl,
          consultation_type: consultationType === 'personne' ? 'consultation_humaine' : 'consultation_animale'
        }),
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la création de la session de paiement');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
      setIsLoading(false);
    }
  };

  // Reset state when modal closes
  const handleClose = () => {
    setIsChecked(false);
    setConsultationType(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-dark-card border border-gold-muted/30 shadow-2xl max-w-2xl rounded-none modal-animate-in max-h-[90vh] overflow-y-auto" data-testid="conditions-validation-modal">
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-cream text-center">
            Conditions de validation de la consultation
          </DialogTitle>
        </DialogHeader>

        {/* Decorative Line */}
        <div className="flex justify-center my-4">
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-sepia to-transparent"></div>
        </div>

        <div className="py-6 font-cormorant text-cream/90 space-y-6 text-lg leading-relaxed">
          <div className="bg-dark-bg/50 p-6 border border-gold-muted/30">
            <p className="mb-4">
              Je reconnais avoir bénéficié des <strong className="text-sepia">10 minutes gratuites</strong>.
            </p>
            <p className="mb-4">
              Je confirme souhaiter poursuivre la consultation au-delà de ces 10 minutes et accepte le paiement de <strong className="text-sepia">800€</strong>.
            </p>
            <p>
              Je reconnais que cette validation constitue un <strong className="text-cream">engagement ferme et définitif</strong> et qu'aucun remboursement ne pourra être demandé après la réalisation de la prestation.
            </p>
          </div>

          {/* Consultation Type - Radio Buttons */}
          <div className="space-y-3">
            <p className="font-playfair text-cream text-base mb-3">Type de consultation :</p>
            
            <label 
              className={`flex items-center gap-4 cursor-pointer p-4 border transition-colors ${
                consultationType === 'personne' 
                  ? 'border-sepia bg-dark-bg/70' 
                  : 'border-gold-muted/30 bg-dark-bg/30 hover:bg-dark-bg/50'
              }`}
            >
              <input 
                type="radio"
                name="consultationType"
                value="personne"
                checked={consultationType === 'personne'}
                onChange={(e) => setConsultationType(e.target.value)}
                className="w-5 h-5 accent-sepia cursor-pointer flex-shrink-0"
                disabled={isLoading}
              />
              <div className="flex items-center gap-3">
                <User size={20} className="text-sepia" />
                <span className="text-cream/90">Une consultation pour une personne</span>
              </div>
            </label>

            <label 
              className={`flex items-center gap-4 cursor-pointer p-4 border transition-colors ${
                consultationType === 'animal' 
                  ? 'border-sepia bg-dark-bg/70' 
                  : 'border-gold-muted/30 bg-dark-bg/30 hover:bg-dark-bg/50'
              }`}
            >
              <input 
                type="radio"
                name="consultationType"
                value="animal"
                checked={consultationType === 'animal'}
                onChange={(e) => setConsultationType(e.target.value)}
                className="w-5 h-5 accent-sepia cursor-pointer flex-shrink-0"
                disabled={isLoading}
              />
              <div className="flex items-center gap-3">
                <PawPrint size={20} className="text-sepia" />
                <span className="text-cream/90">Une consultation pour un animal</span>
              </div>
            </label>
          </div>

          {/* Acceptance Checkbox */}
          <label className={`flex items-start gap-4 cursor-pointer p-4 border transition-colors ${
            isChecked 
              ? 'border-sepia bg-dark-bg/70' 
              : 'border-gold-muted/30 bg-dark-bg/30 hover:bg-dark-bg/50'
          }`}>
            <input 
              type="checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              className="w-6 h-6 mt-1 accent-sepia cursor-pointer flex-shrink-0"
              disabled={isLoading}
            />
            <span className="text-cream/90">
              J'ai lu et j'accepte les conditions de validation de la consultation
            </span>
          </label>

          {/* Error message */}
          {error && (
            <div className="p-4 bg-red-900/30 border border-red-500/50 text-red-300 text-center">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-3">
          {/* Payment Button */}
          <Button 
            onClick={handlePayment}
            disabled={!canProceed || isLoading}
            className={`w-full font-playfair tracking-wider py-6 rounded-none transition-all ${
              canProceed && !isLoading
                ? 'bg-sepia text-dark-bg hover:bg-gold-soft hover:-translate-y-0.5 hover:shadow-lg hover:shadow-sepia/20' 
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="payment-button"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Redirection vers le paiement...
              </span>
            ) : (
              <span className="flex flex-col items-center gap-1">
                <span className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Procéder au paiement sécurisé - 800€
                </span>
                <span className="text-xs font-lato tracking-[0.2em] opacity-80">BRICEMEDIUM.FR</span>
              </span>
            )}
          </Button>

          {/* Close Button */}
          <Button 
            onClick={handleClose}
            variant="outline"
            disabled={isLoading}
            className="w-full bg-transparent border border-gold-muted/50 text-cream/70 hover:text-cream hover:bg-dark-secondary font-cormorant tracking-wide py-3 rounded-none"
          >
            Annuler
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Payment Status Modal Component
const PaymentStatusModal = ({ status, message, onClose }) => {
  if (!status) return null;

  return (
    <Dialog open={!!status} onOpenChange={onClose}>
      <DialogContent className="bg-dark-card border border-gold-muted/30 shadow-2xl max-w-md rounded-none modal-animate-in" data-testid="payment-status-modal">
        <div className="py-8 text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            {status === 'checking' && (
              <div className="w-20 h-20 rounded-full bg-dark-bg border-2 border-sepia flex items-center justify-center">
                <svg className="animate-spin h-10 w-10 text-sepia" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            )}
            {status === 'success' && (
              <div className="w-20 h-20 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center">
                <CheckCircle size={40} className="text-green-500" />
              </div>
            )}
            {status === 'cancelled' && (
              <div className="w-20 h-20 rounded-full bg-orange-900/30 border-2 border-orange-500 flex items-center justify-center">
                <X size={40} className="text-orange-500" />
              </div>
            )}
          </div>

          {/* Title */}
          <h3 className="font-playfair text-2xl text-cream mb-4">
            {status === 'checking' && 'Vérification en cours...'}
            {status === 'success' && 'Paiement réussi !'}
            {status === 'cancelled' && 'Paiement annulé'}
          </h3>

          {/* Message */}
          <p className="font-cormorant text-lg text-cream/80 leading-relaxed px-4">
            {message}
          </p>
        </div>

        {status !== 'checking' && (
          <Button 
            onClick={onClose}
            className="w-full bg-sepia text-dark-bg hover:bg-gold-soft font-playfair tracking-wider py-4 rounded-none"
          >
            {status === 'success' ? 'Continuer' : 'Fermer'}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Cookie Consent Banner Component
const CookieConsent = ({ onAccept, onRefuse, onCustomize, onOpenConfidentialite }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });
  const { t } = useLanguage();

  return (
    <div className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      
      {/* Cookie Banner */}
      <div className="relative bg-dark-card border border-gold-muted/30 shadow-2xl w-full max-w-lg p-6 sm:p-8 animate-fadeInUp">
        {/* Icon - Crystal Ball instead of figurines */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-dark-bg border border-gold-muted/50 flex items-center justify-center">
            <Sparkles size={32} className="text-sepia" />
          </div>
        </div>

        <h3 className="font-playfair text-xl sm:text-2xl text-cream text-center mb-3">
          {t('cookies.title')}
        </h3>
        
        <p className="font-cormorant text-base text-cream/80 text-center mb-6 leading-relaxed">
          {t('cookies.description')}
        </p>

        {/* Details Section */}
        {showDetails && (
          <div className="mb-6 space-y-3 border-t border-b border-gold-muted/20 py-4">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="font-cormorant text-cream/90">Cookies nécessaires</span>
              <input 
                type="checkbox" 
                checked={preferences.necessary} 
                disabled 
                className="w-5 h-5 accent-sepia"
              />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="font-cormorant text-cream/90">Cookies analytiques</span>
              <input 
                type="checkbox" 
                checked={preferences.analytics}
                onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                className="w-5 h-5 accent-sepia cursor-pointer"
              />
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="font-cormorant text-cream/90">Cookies marketing</span>
              <input 
                type="checkbox" 
                checked={preferences.marketing}
                onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                className="w-5 h-5 accent-sepia cursor-pointer"
              />
            </label>
          </div>
        )}

        {/* Buttons */}
        <div className="space-y-3">
          <Button 
            onClick={onAccept}
            className="w-full bg-sepia text-dark-bg hover:bg-gold-soft font-playfair tracking-wider py-4 rounded-none"
          >
            {t('cookies.acceptAll')}
          </Button>
          
          <div className="flex gap-3">
            <Button 
              onClick={() => setShowDetails(!showDetails)}
              variant="outline"
              className="flex-1 bg-transparent border border-gold-muted/50 text-cream hover:bg-dark-secondary font-cormorant tracking-wide py-3 rounded-none"
            >
              {showDetails ? t('common.close') : t('cookies.customize')}
            </Button>
            
            {showDetails ? (
              <Button 
                onClick={() => onCustomize(preferences)}
                className="flex-1 bg-dark-secondary border border-gold-muted/50 text-sepia hover:bg-dark-bg font-cormorant tracking-wide py-3 rounded-none"
              >
                {t('common.save')}
              </Button>
            ) : (
              <Button 
                onClick={onRefuse}
                variant="outline"
                className="flex-1 bg-transparent border border-gold-muted/50 text-cream/70 hover:text-cream hover:bg-dark-secondary font-cormorant tracking-wide py-3 rounded-none"
              >
                {t('cookies.refuse')}
              </Button>
            )}
          </div>
        </div>

        {/* Privacy Link */}
        <p className="font-cormorant text-xs text-cream/50 text-center mt-4">
          {t('cookies.learnMore')}{' '}
          <button 
            onClick={onOpenConfidentialite}
            className="text-sepia hover:underline"
          >
            {t('cookies.privacyPolicy')}
          </button>
        </p>
      </div>
    </div>
  );
};

// ============ MUR DU SOUVENIR (MEMORIAL WALL) ============

// Data for the memorial wall - 41 entries (29 humans + 12 animals)
const memorialData = [
  // Humans (27)
  { id: 3, name: "Françoise", type: "humain", image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=300&h=300&fit=crop" },
  { id: 4, name: "Michel", type: "humain", image: "https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=300&h=300&fit=crop" },
  { id: 5, name: "Monique", type: "humain", image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=300&h=300&fit=crop" },
  { id: 9, name: "Suzanne", type: "humain", image: "https://images.unsplash.com/photo-1593100126453-19b562a800c1?w=300&h=300&fit=crop" },
  { id: 10, name: "Léo", type: "humain", image: "https://images.unsplash.com/photo-1545177251-f63921bd2b2a?crop=entropy&cs=srgb&fm=jpg&w=300&h=300&fit=crop" },
  { id: 12, name: "Pierre", type: "humain", image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=300&h=300&fit=crop" },
  { id: 13, name: "Simone", type: "humain", image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300&h=300&fit=crop" },
  { id: 14, name: "Louis", type: "humain", image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=300&h=300&fit=crop" },
  { id: 15, name: "Jacqueline", type: "humain", image: "https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=300&h=300&fit=crop" },
  { id: 18, name: "Tom", type: "humain", image: "https://images.pexels.com/photos/13183281/pexels-photo-13183281.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&dpr=1&fit=crop" },
  { id: 19, name: "Paulette", type: "humain", image: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=300&h=300&fit=crop" },
  { id: 20, name: "Raymond", type: "humain", image: "https://images.unsplash.com/photo-1545167622-3a6ac756afa4?w=300&h=300&fit=crop" },
  { id: 21, name: "Roger", type: "humain", image: "https://images.unsplash.com/photo-1444069069008-83a57aac43ac?crop=entropy&cs=srgb&fm=jpg&w=300&h=300&fit=crop" },
  { id: 22, name: "Maurice", type: "humain", image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=300&h=300&fit=crop" },
  { id: 24, name: "Mathieu", type: "humain", image: "https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=300&h=300&fit=crop" },
  { id: 25, name: "Catherine", type: "humain", image: "https://images.unsplash.com/photo-1629540266304-fff9c67b7660?w=300&h=300&fit=crop" },
  { id: 28, name: "Émile", type: "humain", image: "https://images.unsplash.com/photo-1504257432389-52343af06ae3?w=300&h=300&fit=crop" },
  { id: 30, name: "Yvonne", type: "humain", image: "https://images.unsplash.com/photo-1498757581981-8ddb3c0b9b07?crop=entropy&cs=srgb&fm=jpg&w=300&h=300&fit=crop" },
  { id: 32, name: "Capucine", type: "humain", image: "https://images.pexels.com/photos/11377320/pexels-photo-11377320.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&dpr=1&fit=crop" },
  { id: 33, name: "Bernadette", type: "humain", image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=300&h=300&fit=crop" },
  { id: 37, name: "Hélène", type: "humain", image: "https://images.unsplash.com/photo-1534751516642-a1af1ef26a56?w=300&h=300&fit=crop" },
  // Animals (11)
  { id: 40, name: "Max", type: "animal", image: "https://images.unsplash.com/photo-1558788353-f76d92427f16?w=300&h=300&fit=crop" },
  { id: 41, name: "Minou", type: "animal", image: "https://images.unsplash.com/photo-1647806422508-0322f33e270b?w=300&h=300&fit=crop" },
  { id: 44, name: "Lucky", type: "animal", image: "https://images.unsplash.com/photo-1612940960267-4549a58fb257?w=300&h=300&fit=crop" },
  { id: 46, name: "Belle", type: "animal", image: "https://images.unsplash.com/photo-1629740067905-bd3f515aa739?w=300&h=300&fit=crop" },
  { id: 47, name: "Félix", type: "animal", image: "https://images.unsplash.com/photo-1608032364895-0da67af36cd2?w=300&h=300&fit=crop" },
  { id: 48, name: "Oscar", type: "animal", image: "https://images.unsplash.com/photo-1581888227599-779811939961?w=300&h=300&fit=crop" },
  { id: 49, name: "Nala", type: "animal", image: "https://images.unsplash.com/photo-1518791841217-8f162f1e1131?w=300&h=300&fit=crop" },
  { id: 50, name: "Buddy", type: "animal", image: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=300&h=300&fit=crop" },
  { id: 51, name: "Minette", type: "animal", image: "https://images.unsplash.com/photo-1495360010541-f48722b34f7d?w=300&h=300&fit=crop" },
  { id: 52, name: "Médor", type: "animal", image: "https://images.unsplash.com/photo-1544568100-847a948585b9?w=300&h=300&fit=crop" },
  // Featured tributes with captions
  { id: 100, name: "Papa", type: "humain", caption: '"Papa est fier de toi"', image: "https://images.unsplash.com/photo-1601779805802-d8363cd70ccc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxlbGRlcmx5JTIwZ3JhbmRmYXRoZXIlMjBwb3J0cmFpdCUyMHZpbnRhZ2UlMjBzZXBpYXxlbnwwfHx8fDE3NzM1MTU1NjR8MA&ixlib=rb-4.1.0&q=85" },
  { id: 101, name: "Minou", type: "animal", caption: '"Minou veille sur ta fille"', image: "https://images.unsplash.com/photo-1741700840853-6c35a47b4e2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxjYXQlMjBwb3J0cmFpdCUyMHBlYWNlZnVsfGVufDB8fHx8MTc3MzUxNTU2N3ww&ixlib=rb-4.1.0&q=85" },
  { id: 102, name: "Maman", type: "humain", caption: '"Maman t\'envoie du lilas"', image: "https://images.pexels.com/photos/3768131/pexels-photo-3768131.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { id: 103, name: "Rex", type: "animal", caption: '"Rex attend tes câlins"', image: "https://images.unsplash.com/photo-1597205819209-a999c84568d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxnb2xkZW4lMjByZXRyaWV2ZXIlMjBkb2clMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzM1MTU1Njd8MA&ixlib=rb-4.1.0&q=85" },
];

// Modal for adding a photo to the memorial wall
const AjouterPhotoModal = ({ isOpen, onClose }) => {
  const formRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [defuntType, setDefuntType] = useState('');
  const [isConsented, setIsConsented] = useState(false);
  const [fileName, setFileName] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5242880) {
        setSubmitStatus('La taille du fichier ne doit pas dépasser 5MB');
        e.target.value = '';
        setFileName('');
        return;
      }
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
        setSubmitStatus('Seuls les formats JPEG, PNG et GIF sont acceptés');
        e.target.value = '';
        setFileName('');
        return;
      }
      setFileName(file.name);
      setSubmitStatus('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isConsented) {
      setSubmitStatus('Veuillez accepter les conditions de consentement');
      return;
    }

    if (!defuntType) {
      setSubmitStatus('Veuillez sélectionner le type (Humain ou Animal)');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Check if EmailJS is configured
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;

      if (!publicKey || publicKey === 'your_public_key_here' || 
          !serviceId || serviceId === 'your_service_id_here' ||
          !templateId || templateId === 'your_template_id_here') {
        // EmailJS not configured - show success message anyway for demo
        console.log('EmailJS not configured - demo mode');
        setSubmitStatus('success');
        setTimeout(() => {
          handleClose();
        }, 3000);
        return;
      }

      // Send via EmailJS
      await emailjs.sendForm(
        serviceId,
        templateId,
        formRef.current,
        publicKey
      );

      setSubmitStatus('success');
      setTimeout(() => {
        handleClose();
      }, 3000);

    } catch (error) {
      console.error('Error sending form:', error);
      setSubmitStatus('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setDefuntType('');
    setIsConsented(false);
    setFileName('');
    setSubmitStatus('');
    formRef.current?.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-dark-card border border-gold-muted/30 shadow-2xl max-w-lg rounded-none modal-animate-in max-h-[90vh] overflow-y-auto" data-testid="ajouter-photo-modal">
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-cream text-center">
            Ajouter une photo au Mur du Souvenir
          </DialogTitle>
        </DialogHeader>

        {/* Decorative Line */}
        <div className="flex justify-center my-4">
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-sepia to-transparent"></div>
        </div>

        {submitStatus === 'success' ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center">
              <CheckCircle size={32} className="text-green-500" />
            </div>
            <h3 className="font-playfair text-xl text-cream mb-4">Demande envoyée !</h3>
            <p className="font-cormorant text-cream/80 text-lg">
              Vos informations restent strictement confidentielles et ne seront jamais partagées.
            </p>
          </div>
        ) : (
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
            {/* Prénom du défunt */}
            <div>
              <label className="block font-cormorant text-cream/80 mb-2">
                Prénom du défunt *
              </label>
              <input
                type="text"
                name="prenom_defunt"
                required
                placeholder="Entrez le prénom"
                disabled={isSubmitting}
                className="w-full bg-dark-bg border border-gold-muted/30 text-cream p-3 rounded focus:border-sepia outline-none font-cormorant"
              />
            </div>

            {/* Type: Humain ou Animal */}
            <div>
              <label className="block font-cormorant text-cream/80 mb-2">
                Type *
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className={`flex items-center justify-center gap-2 p-3 border cursor-pointer transition-colors ${
                  defuntType === 'humain' 
                    ? 'border-sepia bg-dark-bg/70 text-sepia' 
                    : 'border-gold-muted/30 bg-dark-bg/30 text-cream/70 hover:bg-dark-bg/50'
                }`}>
                  <input
                    type="radio"
                    name="type_defunt"
                    value="humain"
                    checked={defuntType === 'humain'}
                    onChange={(e) => setDefuntType(e.target.value)}
                    className="sr-only"
                    disabled={isSubmitting}
                  />
                  <User size={20} />
                  <span className="font-cormorant">Humain</span>
                </label>
                <label className={`flex items-center justify-center gap-2 p-3 border cursor-pointer transition-colors ${
                  defuntType === 'animal' 
                    ? 'border-sepia bg-dark-bg/70 text-sepia' 
                    : 'border-gold-muted/30 bg-dark-bg/30 text-cream/70 hover:bg-dark-bg/50'
                }`}>
                  <input
                    type="radio"
                    name="type_defunt"
                    value="animal"
                    checked={defuntType === 'animal'}
                    onChange={(e) => setDefuntType(e.target.value)}
                    className="sr-only"
                    disabled={isSubmitting}
                  />
                  <PawPrint size={20} />
                  <span className="font-cormorant">Animal</span>
                </label>
              </div>
            </div>

            {/* Upload Photo */}
            <div>
              <label className="block font-cormorant text-cream/80 mb-2">
                Photo *
              </label>
              <div className="relative">
                <input
                  type="file"
                  name="photo"
                  accept="image/jpeg,image/png,image/gif"
                  required
                  onChange={handleFileChange}
                  disabled={isSubmitting}
                  className="w-full bg-dark-bg border border-gold-muted/30 text-cream p-3 rounded focus:border-sepia outline-none font-cormorant file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-sepia file:text-dark-bg file:font-cormorant file:cursor-pointer"
                />
              </div>
              {fileName && (
                <p className="mt-2 font-cormorant text-sepia text-sm">
                  Fichier sélectionné : {fileName}
                </p>
              )}
              <p className="mt-1 font-cormorant text-cream/50 text-xs">
                Formats acceptés : JPEG, PNG, GIF (max 5MB)
              </p>
            </div>

            {/* Email (hidden, for EmailJS) */}
            <input type="hidden" name="to_email" value="contact@bricemedium.fr" />

            {/* Consentement */}
            <label className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors ${
              isConsented 
                ? 'border-sepia bg-dark-bg/70' 
                : 'border-gold-muted/30 bg-dark-bg/30 hover:bg-dark-bg/50'
            }`}>
              <input
                type="checkbox"
                name="consentement"
                checked={isConsented}
                onChange={(e) => setIsConsented(e.target.checked)}
                className="w-5 h-5 mt-0.5 accent-sepia cursor-pointer flex-shrink-0"
                disabled={isSubmitting}
              />
              <span className="font-cormorant text-cream/80 text-sm leading-relaxed">
                Je certifie être un proche du défunt et autorise <strong>BRICEMEDIUM.FR</strong> à publier cette photo sur le Mur du Souvenir. Je comprends que je peux demander le retrait à tout moment en contactant <span className="text-sepia">contact@bricemedium.fr</span>
              </span>
            </label>

            {/* Error message */}
            {submitStatus && submitStatus !== 'success' && (
              <div className="p-3 bg-red-900/30 border border-red-500/50 text-red-300 text-center font-cormorant rounded text-sm">
                {submitStatus}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting || !isConsented || !defuntType}
              className={`w-full py-4 font-playfair tracking-wider rounded-none transition-all ${
                isConsented && defuntType && !isSubmitting
                  ? 'bg-sepia text-dark-bg hover:bg-gold-soft'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Envoi en cours...
                </span>
              ) : (
                'Envoyer ma demande'
              )}
            </Button>

            {/* Cancel Button */}
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              disabled={isSubmitting}
              className="w-full bg-transparent border border-gold-muted/50 text-cream/70 hover:text-cream hover:bg-dark-secondary font-cormorant tracking-wide py-3 rounded-none"
            >
              Annuler
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Memorial Wall Section Component
const MurDuSouvenir = ({ onOpenAjouterPhoto }) => {
  const { t } = useLanguage();
  
  return (
    <section id="mur-du-souvenir" className="py-20 bg-dark-bg relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-sepia rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gold-muted rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          {/* Mobile title */}
          <h2 className="md:hidden font-playfair text-4xl text-sepia mb-4">
            Hommages<br />aux Êtres Chers
          </h2>
          {/* Desktop/Tablet title */}
          <h2 className="hidden md:block font-playfair text-5xl text-sepia mb-4">
            {t('memorial.title')}
          </h2>
          <p className="font-cormorant italic text-xl md:text-2xl text-white mb-4">
            {t('memorial.tagline')}
          </p>
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-sepia to-transparent mx-auto mb-6"></div>
          {/* Mobile version */}
          <p className="md:hidden font-cormorant text-xl text-cream/80 max-w-3xl mx-auto leading-relaxed">
            Avec leur accord et celui de leurs familles,<br />ces photos témoignent des moments<br />de connexion et de réconfort partagés<br />lors de nos consultations.<br /><br />Chaque être cher reste présent<br />dans nos cœurs.
          </p>
          {/* Desktop/Tablet version with line break */}
          <p className="hidden md:block font-cormorant text-xl text-cream/80 max-w-3xl mx-auto leading-relaxed">
            Avec leur accord et celui de leurs familles, ces photos témoignent des moments de connexion<br />et de réconfort partagés lors de nos consultations. Chaque être cher reste présent dans nos cœurs.
          </p>
        </div>

        {/* Photo Grid */}
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-13 gap-2 mb-12">
          {memorialData.map((person) => (
            <div
              key={person.id}
              className="group relative aspect-square overflow-hidden border border-gold-muted/20 hover:border-sepia transition-all duration-300"
            >
              <img
                src={person.image}
                alt={person.name}
                className="w-full h-full object-cover filter grayscale group-hover:grayscale-0 transition-all duration-500"
                loading="lazy"
              />
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/90 via-dark-bg/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-2">
                <p
                  className={`font-cormorant text-cream text-center ${person.caption ? 'text-[10px] italic leading-tight' : 'text-xs truncate'}`}
                  title={person.caption || person.name}
                >
                  {person.caption || person.name}
                </p>
                <p className="text-center">
                  {person.type === 'animal' ? (
                    <PawPrint size={12} className="inline text-sepia" />
                  ) : (
                    <Heart size={12} className="inline text-sepia" />
                  )}
                </p>
              </div>
              {/* Golden frame effect on hover */}
              <div className="absolute inset-0 border-2 border-sepia/0 group-hover:border-sepia/50 transition-all duration-300"></div>
            </div>
          ))}
        </div>

        {/* Add Photo Button */}
        <div className="text-center">
          {/* Mobile version - enlarged with line break */}
          <p className="md:hidden mb-4 font-cormorant text-white text-base">
            Si vous souhaitez que votre être cher figure<br />sur ce mur du souvenir, cliquez ci-dessous.
          </p>
          {/* Desktop/Tablet version */}
          <p className="hidden md:block mb-4 font-cormorant text-white md:text-base lg:text-lg">
            {t('memorial.addPhotoDesc')}
          </p>
          <button
            onClick={onOpenAjouterPhoto}
            className="inline-flex items-center gap-3 bg-transparent border-2 border-sepia text-sepia hover:bg-sepia hover:text-dark-bg font-playfair tracking-wider py-4 px-8 transition-all duration-300 group"
          >
            <Heart size={20} className="group-hover:scale-110 transition-transform" />
            {t('memorial.addPhoto')}
          </button>
        </div>
      </div>
    </section>
  );
};

// ============ NEWSLETTER SECTION ============

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(''); // 'success', 'error', ''
  const [message, setMessage] = useState('');
  const { t } = useLanguage();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage(t('contact.error'));
      return;
    }

    setIsSubmitting(true);
    setStatus('');
    setMessage('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage(t('newsletter.success'));
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.message || t('contact.error'));
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setStatus('error');
      setMessage(t('contact.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="newsletter" className="py-16 bg-gradient-to-b from-dark-bg to-dark-secondary relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-sepia rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-sepia/10 border border-sepia flex items-center justify-center">
            <Mail size={28} className="text-sepia" />
          </div>

          {/* Title */}
          <h2 className="font-playfair text-3xl md:text-4xl text-sepia mb-3">
            {t('newsletter.title')}
          </h2>
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-sepia to-transparent mx-auto mb-4"></div>
          {/* Mobile version - more line breaks */}
          <p className="md:hidden font-cormorant text-lg text-cream/80 mb-8">
            Une fois par mois,<br />recevez quelques précieux témoignages<br />transmis par les défunts,<br />dans le respect de leur anonymat,<br />et découvrez l'émotion de renouer le lien<br />avec ceux qui nous ont quittés.
          </p>
          {/* Desktop/Tablet version */}
          <p className="hidden md:block font-cormorant text-lg text-cream/80 mb-8 whitespace-pre-line">
            {t('newsletter.subtitle')}
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('newsletter.placeholder')}
              disabled={isSubmitting}
              className="flex-1 bg-dark-bg border border-gold-muted/30 text-cream px-4 py-3 focus:border-sepia outline-none font-cormorant text-lg placeholder:text-white"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-8 py-3 font-playfair tracking-wider transition-all ${
                isSubmitting
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-sepia text-dark-bg hover:bg-gold-soft'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  ...
                </span>
              ) : (
                t('newsletter.button')
              )}
            </button>
          </form>

          {/* Status Message */}
          {status && (
            <div className={`mt-4 p-3 rounded font-cormorant ${
              status === 'success' 
                ? 'bg-green-900/30 border border-green-500/50 text-green-300' 
                : 'bg-red-900/30 border border-red-500/50 text-red-300'
            }`}>
              {message}
            </div>
          )}

          {/* Privacy note */}
          {/* Mobile version - enlarged with line break */}
          <p className="md:hidden mt-6 font-cormorant text-cream/70 text-lg">
            Vos données restent confidentielles<br />et ne seront jamais partagées.
          </p>
          {/* Desktop/Tablet version */}
          <p className="hidden md:block mt-6 font-cormorant text-cream/70 text-lg">
            {t('newsletter.privacy')}
          </p>
        </div>
      </div>
    </section>
  );
};

// ============ ADMIN LOGIN PAGE ============

const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (data.success && data.token) {
        // Store token in sessionStorage
        sessionStorage.setItem('admin_token', data.token);
        onLogin(data.token);
      } else {
        setError(data.message || 'Erreur de connexion');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Erreur de connexion au serveur');
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <h1 className="font-playfair text-3xl text-sepia mb-2">Administration</h1>
          <p className="font-cormorant text-cream/70">BRICEMEDIUM.FR</p>
        </div>

        {/* Login Form */}
        <div className="bg-dark-card border border-gold-muted/30 p-8 rounded">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block font-cormorant text-cream/70 text-sm mb-2">
                Identifiant
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-dark-bg border border-gold-muted/30 text-cream p-3 rounded focus:border-sepia outline-none font-cormorant"
                placeholder="Entrez votre identifiant"
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block font-cormorant text-cream/70 text-sm mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-dark-bg border border-gold-muted/30 text-cream p-3 pr-12 rounded focus:border-sepia outline-none font-cormorant"
                  placeholder="Entrez votre mot de passe"
                  required
                  disabled={isLoading}
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-cream/60 hover:text-sepia transition-colors p-1"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  tabIndex={-1}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-900/30 border border-red-500/50 text-red-300 text-center font-cormorant rounded">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 font-playfair tracking-wider rounded transition-all ${
                isLoading
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-sepia text-dark-bg hover:bg-gold-soft'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Connexion...
                </span>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <a
              href="/"
              className="font-cormorant text-cream/50 hover:text-sepia transition-colors text-sm"
            >
              ← Retour au site
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ ADMIN DASHBOARD ============

const ChangePasswordCard = ({ onSuccess }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text: string }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    // Client-side validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'Veuillez remplir tous les champs.' });
      return;
    }
    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Le nouveau mot de passe doit contenir au moins 8 caractères.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Le nouveau mot de passe et sa confirmation ne correspondent pas.' });
      return;
    }
    if (newPassword === currentPassword) {
      setMessage({ type: 'error', text: "Le nouveau mot de passe doit être différent de l'ancien." });
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: data.message || 'Mot de passe modifié avec succès.' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        if (onSuccess) onSuccess();
      } else {
        setMessage({ type: 'error', text: data.message || 'Une erreur est survenue.' });
      }
    } catch (err) {
      console.error('Change password error:', err);
      setMessage({ type: 'error', text: 'Erreur de connexion au serveur.' });
    } finally {
      setLoading(false);
    }
  };

  const renderPasswordInput = (label, value, setValue, show, setShow, autoComplete) => (
    <div>
      <label className="block font-cormorant text-cream/80 text-base mb-2">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete={autoComplete}
          className="w-full bg-dark-bg border border-gold-muted/40 focus:border-sepia focus:outline-none rounded px-4 py-2.5 pr-12 text-cream font-cormorant transition-colors"
          disabled={loading}
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-cream/50 hover:text-sepia transition-colors"
          tabIndex={-1}
          aria-label={show ? 'Masquer' : 'Afficher'}
        >
          {show ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-xl mx-auto">
      <div className="bg-dark-card border border-gold-muted/30 rounded-lg p-8 shadow-xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-sepia/10 border border-sepia/40 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-sepia" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <div>
            <h2 className="font-playfair text-xl text-sepia">Changer mon mot de passe</h2>
            <p className="font-cormorant text-cream/60 text-base">Sécurité du compte administrateur</p>
          </div>
        </div>

        <div className="border-t border-gold-muted/20 my-6"></div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {renderPasswordInput("Mot de passe actuel", currentPassword, setCurrentPassword, showCurrent, setShowCurrent, "current-password")}
          {renderPasswordInput("Nouveau mot de passe", newPassword, setNewPassword, showNew, setShowNew, "new-password")}
          {renderPasswordInput("Confirmer le nouveau mot de passe", confirmPassword, setConfirmPassword, showConfirm, setShowConfirm, "new-password")}

          <p className="font-cormorant text-sm text-cream/50 italic">
            8 caractères minimum. Le nouveau mot de passe est chiffré (bcrypt) avant d'être enregistré, jamais stocké en clair.
          </p>

          {message && (
            <div
              className={`rounded px-4 py-3 font-cormorant text-base border ${
                message.type === 'success'
                  ? 'bg-green-900/20 border-green-500/40 text-green-300'
                  : 'bg-red-900/20 border-red-500/40 text-red-300'
              }`}
            >
              {message.text}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-sepia hover:bg-sepia/90 disabled:bg-sepia/40 disabled:cursor-not-allowed text-dark-bg font-cormorant font-semibold text-base py-3 rounded transition-colors"
          >
            {loading ? 'Modification en cours...' : 'Mettre à jour le mot de passe'}
          </button>
        </form>
      </div>
    </div>
  );
};


const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [activeTab, setActiveTab] = useState('payments'); // 'payments', 'contacts', 'newsletter'
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [revenueByYear, setRevenueByYear] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState({ subscribers: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    payment_status: '',
    consultation_type: '',
    date_from: '',
    date_to: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Get auth token
  const getToken = () => sessionStorage.getItem('admin_token');

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      if (token) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/admin/verify`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await response.json();
          if (data.valid) {
            setIsAuthenticated(true);
          } else {
            sessionStorage.removeItem('admin_token');
          }
        } catch (err) {
          console.error('Auth check error:', err);
          sessionStorage.removeItem('admin_token');
        }
      }
      setAuthChecking(false);
    };
    checkAuth();
  }, [BACKEND_URL]);

  // Handle login
  const handleLogin = (token) => {
    setIsAuthenticated(true);
  };

  // Handle logout
  const handleLogout = async () => {
    const token = getToken();
    try {
      await fetch(`${BACKEND_URL}/api/admin/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
    sessionStorage.removeItem('admin_token');
    setIsAuthenticated(false);
  };

  // Fetch transactions with filters
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.payment_status) params.append('payment_status', filters.payment_status);
      if (filters.consultation_type) params.append('consultation_type', filters.consultation_type);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const response = await fetch(`${BACKEND_URL}/api/payments/transactions?${params}`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.status === 401) {
        sessionStorage.removeItem('admin_token');
        setIsAuthenticated(false);
        return;
      }
      
      const data = await response.json();
      setTransactions(data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
    setLoading(false);
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/stats`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.status === 401) {
        sessionStorage.removeItem('admin_token');
        setIsAuthenticated(false);
        return;
      }
      
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch revenue by year
  const fetchRevenueByYear = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/revenue-by-year`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.status === 401) {
        sessionStorage.removeItem('admin_token');
        setIsAuthenticated(false);
        return;
      }
      
      const data = await response.json();
      setRevenueByYear(data);
    } catch (error) {
      console.error('Error fetching revenue by year:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchTransactions();
      fetchStats();
      fetchRevenueByYear();
      fetchContacts();
      fetchNewsletter();
    }
  }, [isAuthenticated]);

  // Fetch contacts
  const fetchContacts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/contacts`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.status === 401) {
        sessionStorage.removeItem('admin_token');
        setIsAuthenticated(false);
        return;
      }
      const data = await response.json();
      setContacts(data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  // Fetch newsletter subscribers
  const fetchNewsletter = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/newsletter`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.status === 401) {
        sessionStorage.removeItem('admin_token');
        setIsAuthenticated(false);
        return;
      }
      const data = await response.json();
      setNewsletter(data);
    } catch (error) {
      console.error('Error fetching newsletter:', error);
    }
  };

  // Mark contact as read
  const handleMarkContactRead = async (contactId) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/contacts/${contactId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      fetchContacts();
    } catch (error) {
      console.error('Error marking contact as read:', error);
    }
  };

  // Delete contact
  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  // Delete newsletter subscriber
  const handleDeleteSubscriber = async (subscriberId) => {
    if (!window.confirm('Supprimer cet abonné ?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/newsletter/${subscriberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      fetchNewsletter();
    } catch (error) {
      console.error('Error deleting subscriber:', error);
    }
  };

  // Export contacts to CSV (Excel-compatible, UTF-8 BOM, semicolon separator for FR Excel)
  const handleExportContactsCSV = () => {
    if (!contacts || contacts.length === 0) {
      alert('Aucun contact à exporter.');
      return;
    }

    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '';
      const s = String(val).replace(/"/g, '""');
      return `"${s}"`;
    };

    const headers = [
      'Date',
      'Prénom',
      'Email',
      'Téléphone',
      'Type',
      'Type consultation',
      'Jours préférés',
      'Horaires préférés',
      'Ville',
      'Département',
      'Message',
      'Commentaire',
      'Photo jointe',
      'Lu',
    ];

    const rows = contacts.map((c) => [
      c.created_at ? new Date(c.created_at).toLocaleString('fr-FR') : '',
      c.prenom || '',
      c.email || '',
      c.telephone || '',
      c.source === 'booking' ? 'Demande RDV' : 'Message',
      c.consultation_type === 'humain'
        ? 'Humain'
        : c.consultation_type === 'animal'
        ? 'Animal'
        : '',
      c.preferred_days || '',
      c.preferred_times || '',
      c.ville || '',
      c.departement || '',
      c.message || '',
      c.comment || '',
      c.photo_filename || (c.photo_base64 ? 'Oui' : 'Non'),
      c.is_read ? 'Oui' : 'Non',
    ]);

    const csvContent =
      '\uFEFF' + // UTF-8 BOM for Excel
      [headers, ...rows]
        .map((row) => row.map(escapeCsv).join(';'))
        .join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const today = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `bricemedium-contacts-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export newsletter
  const handleExportNewsletter = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/newsletter/export`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `newsletter_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting newsletter:', error);
    }
  };

  // Apply filters
  const handleApplyFilters = () => {
    fetchTransactions();
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      status: '',
      payment_status: '',
      consultation_type: '',
      date_from: '',
      date_to: ''
    });
    setTimeout(() => fetchTransactions(), 100);
  };

  // Export JSON
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.payment_status) params.append('payment_status', filters.payment_status);
      if (filters.consultation_type) params.append('consultation_type', filters.consultation_type);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const response = await fetch(`${BACKEND_URL}/api/payments/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.status === 401) {
        sessionStorage.removeItem('admin_token');
        setIsAuthenticated(false);
        return;
      }
      
      const data = await response.json();
      
      // Create download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  // Delete/Reset transactions
  const handleReset = async () => {
    const confirmMessage = transactions.length > 0 
      ? `Êtes-vous sûr de vouloir supprimer ${transactions.length} transaction(s) ?\n\nCette action est irréversible.`
      : 'Aucune transaction à supprimer.';
    
    if (transactions.length === 0) {
      alert(confirmMessage);
      return;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.payment_status) params.append('payment_status', filters.payment_status);
      if (filters.consultation_type) params.append('consultation_type', filters.consultation_type);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const response = await fetch(`${BACKEND_URL}/api/payments/transactions?${params}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.status === 401) {
        sessionStorage.removeItem('admin_token');
        setIsAuthenticated(false);
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        alert(data.message);
        // Refresh data
        fetchTransactions();
        fetchStats();
        fetchRevenueByYear();
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // Reset revenue (delete paid transactions)
  const handleResetRevenue = async (year = null) => {
    const yearText = year ? ` pour l'année ${year}` : ' (TOUTES LES ANNÉES)';
    const confirmMessage = `Êtes-vous sûr de vouloir remettre à zéro le chiffre d'affaires${yearText} ?\n\nCela supprimera toutes les transactions payées${yearText}.\n\nCette action est irréversible.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const url = year 
        ? `${BACKEND_URL}/api/payments/revenue-reset?year=${year}`
        : `${BACKEND_URL}/api/payments/revenue-reset`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.status === 401) {
        sessionStorage.removeItem('admin_token');
        setIsAuthenticated(false);
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        alert(data.message);
        // Refresh all data
        fetchTransactions();
        fetchStats();
        fetchRevenueByYear();
      } else {
        alert('Erreur lors de la remise à zéro');
      }
    } catch (error) {
      console.error('Error resetting revenue:', error);
      alert('Erreur lors de la remise à zéro');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status badge
  const StatusBadge = ({ status, type = 'status' }) => {
    const colors = {
      // payment_status
      paid: 'bg-green-600 text-white',
      pending: 'bg-yellow-600 text-white',
      unpaid: 'bg-red-600 text-white',
      expired: 'bg-gray-600 text-white',
      // status
      initiated: 'bg-blue-600 text-white',
      completed: 'bg-green-600 text-white',
      failed: 'bg-red-600 text-white'
    };
    
    return (
      <span className={`px-2 py-1 rounded text-sm font-medium ${colors[status] || 'bg-gray-500 text-white'}`}>
        {status || 'N/A'}
      </span>
    );
  };

  // Show loading while checking auth
  if (authChecking) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-sepia mx-auto mb-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="font-cormorant text-cream/70">Vérification...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-dark-bg text-cream">
      {/* Header */}
      <header className="bg-dark-secondary border-b border-gold-muted/30 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="font-playfair text-2xl text-sepia">Dashboard Admin</h1>
            <p className="font-cormorant text-cream/70">Gestion complète - BRICEMEDIUM.FR</p>
          </div>
          <div className="flex items-center gap-4">
            <a 
              href="/"
              className="font-cormorant text-cream/50 hover:text-sepia transition-colors"
            >
              ← Retour au site
            </a>
            <button
              onClick={handleLogout}
              className="font-cormorant text-red-400 hover:text-red-300 transition-colors flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      {/* Tabs Navigation */}
      <div className="bg-dark-card border-b border-gold-muted/20">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex gap-1">
            <button
              onClick={() => setActiveTab('payments')}
              className={`px-6 py-4 font-cormorant text-base transition-colors border-b-2 ${
                activeTab === 'payments'
                  ? 'border-sepia text-sepia'
                  : 'border-transparent text-cream/60 hover:text-cream'
              }`}
            >
              💳 Paiements
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`px-6 py-4 font-cormorant text-base transition-colors border-b-2 relative ${
                activeTab === 'contacts'
                  ? 'border-sepia text-sepia'
                  : 'border-transparent text-cream/60 hover:text-cream'
              }`}
            >
              📧 Messages
              {contacts.filter(c => !c.is_read).length > 0 && (
                <span className="absolute top-2 right-1 w-5 h-5 bg-red-500 text-white text-sm rounded-full flex items-center justify-center">
                  {contacts.filter(c => !c.is_read).length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('newsletter')}
              className={`px-6 py-4 font-cormorant text-base transition-colors border-b-2 ${
                activeTab === 'newsletter'
                  ? 'border-sepia text-sepia'
                  : 'border-transparent text-cream/60 hover:text-cream'
              }`}
            >
              📰 Newsletter ({newsletter.stats?.active || 0})
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-4 font-cormorant text-base transition-colors border-b-2 ${
                activeTab === 'settings'
                  ? 'border-sepia text-sepia'
                  : 'border-transparent text-cream/60 hover:text-cream'
              }`}
            >
              ⚙️ Paramètres
            </button>
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* PAYMENTS TAB */}
        {activeTab === 'payments' && (
          <>
            {/* Stats Cards */}
            {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-dark-card border border-gold-muted/30 p-4 rounded">
              <p className="font-cormorant text-cream/70 text-base">Total Transactions</p>
              <p className="font-playfair text-2xl text-cream">{stats.total_transactions}</p>
            </div>
            <div className="bg-dark-card border border-green-500/30 p-4 rounded">
              <p className="font-cormorant text-green-400 text-base">Payées</p>
              <p className="font-playfair text-2xl text-green-400">{stats.paid}</p>
            </div>
            <div className="bg-dark-card border border-yellow-500/30 p-4 rounded">
              <p className="font-cormorant text-yellow-400 text-base">En attente</p>
              <p className="font-playfair text-2xl text-yellow-400">{stats.pending}</p>
            </div>
            <div className="bg-dark-card border border-red-500/30 p-4 rounded">
              <p className="font-cormorant text-red-400 text-base">Échouées</p>
              <p className="font-playfair text-2xl text-red-400">{stats.failed}</p>
            </div>
            <div className="bg-dark-card border border-sepia/30 p-4 rounded">
              <p className="font-cormorant text-sepia text-base">Humaines</p>
              <p className="font-playfair text-2xl text-sepia">{stats.by_type?.humaine || 0}</p>
            </div>
            <div className="bg-dark-card border border-sepia/30 p-4 rounded">
              <p className="font-cormorant text-sepia text-base">Animales</p>
              <p className="font-playfair text-2xl text-sepia">{stats.by_type?.animale || 0}</p>
            </div>
          </div>
        )}

        {/* Revenue Card */}
        {stats && (
          <div className="bg-gradient-to-r from-dark-card to-dark-secondary border border-gold-muted/50 p-6 rounded mb-8">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-cormorant text-cream/70">Chiffre d'affaires total (paiements confirmés)</p>
                <p className="font-playfair text-4xl text-sepia">{stats.total_revenue?.toFixed(2)} €</p>
              </div>
              <button
                onClick={() => handleResetRevenue()}
                className="bg-dark-bg border border-red-500 text-red-400 hover:bg-red-500 hover:text-white font-cormorant py-2 px-4 rounded transition-colors flex items-center gap-2 text-base"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                RAZ CA Total
              </button>
            </div>
          </div>
        )}

        {/* Revenue by Year Table */}
        {revenueByYear && revenueByYear.by_year && revenueByYear.by_year.length > 0 && (
          <div className="bg-dark-card border border-gold-muted/30 rounded mb-8 overflow-hidden">
            <div className="p-4 border-b border-gold-muted/20">
              <h2 className="font-playfair text-lg text-sepia">Chiffre d'affaires par année</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-dark-secondary">
                  <tr>
                    <th className="text-left font-playfair text-sepia p-4 text-base">Année</th>
                    <th className="text-left font-playfair text-sepia p-4 text-base">CA Total</th>
                    <th className="text-left font-playfair text-sepia p-4 text-base">Transactions</th>
                    <th className="text-left font-playfair text-sepia p-4 text-base">👤 Humaines</th>
                    <th className="text-left font-playfair text-sepia p-4 text-base">🐾 Animales</th>
                    <th className="text-left font-playfair text-sepia p-4 text-base">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {revenueByYear.by_year.map((yearData, index) => (
                    <tr key={yearData.year || index} className="border-t border-gold-muted/10 hover:bg-dark-bg/50">
                      <td className="p-4 font-playfair text-cream text-lg">{yearData.year}</td>
                      <td className="p-4 font-playfair text-sepia text-xl">{yearData.total_revenue?.toFixed(2)} €</td>
                      <td className="p-4 font-cormorant text-cream/90">{yearData.transaction_count}</td>
                      <td className="p-4 font-cormorant text-cream/90">{yearData.humaine_count}</td>
                      <td className="p-4 font-cormorant text-cream/90">{yearData.animale_count}</td>
                      <td className="p-4">
                        <button
                          onClick={() => handleResetRevenue(yearData.year)}
                          className="bg-dark-bg border border-red-500/50 text-red-400 hover:bg-red-500 hover:text-white font-cormorant py-1 px-3 rounded transition-colors text-base"
                        >
                          RAZ {yearData.year}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {/* Total Row */}
                  <tr className="border-t-2 border-gold-muted/30 bg-dark-secondary/50">
                    <td className="p-4 font-playfair text-cream font-semibold">TOTAL</td>
                    <td className="p-4 font-playfair text-sepia text-xl font-semibold">{revenueByYear.grand_total?.toFixed(2)} €</td>
                    <td className="p-4 font-cormorant text-cream font-semibold">{revenueByYear.total_transactions}</td>
                    <td className="p-4"></td>
                    <td className="p-4"></td>
                    <td className="p-4"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-dark-card border border-gold-muted/30 p-6 rounded mb-8">
          <h2 className="font-playfair text-lg text-sepia mb-4">Filtres</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <label className="block font-cormorant text-cream/70 text-base mb-1">Statut</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="w-full bg-dark-bg border border-gold-muted/30 text-cream p-2 rounded focus:border-sepia outline-none"
              >
                <option value="">Tous</option>
                <option value="initiated">Initié</option>
                <option value="completed">Complété</option>
                <option value="failed">Échoué</option>
              </select>
            </div>
            <div>
              <label className="block font-cormorant text-cream/70 text-base mb-1">Paiement</label>
              <select
                value={filters.payment_status}
                onChange={(e) => setFilters({...filters, payment_status: e.target.value})}
                className="w-full bg-dark-bg border border-gold-muted/30 text-cream p-2 rounded focus:border-sepia outline-none"
              >
                <option value="">Tous</option>
                <option value="paid">Payé</option>
                <option value="pending">En attente</option>
                <option value="unpaid">Non payé</option>
                <option value="expired">Expiré</option>
              </select>
            </div>
            <div>
              <label className="block font-cormorant text-cream/70 text-base mb-1">Type</label>
              <select
                value={filters.consultation_type}
                onChange={(e) => setFilters({...filters, consultation_type: e.target.value})}
                className="w-full bg-dark-bg border border-gold-muted/30 text-cream p-2 rounded focus:border-sepia outline-none"
              >
                <option value="">Tous</option>
                <option value="consultation_humaine">Humaine</option>
                <option value="consultation_animale">Animale</option>
              </select>
            </div>
            <div>
              <label className="block font-cormorant text-cream/70 text-base mb-1">Date début</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                className="w-full bg-dark-bg border border-gold-muted/30 text-cream p-2 rounded focus:border-sepia outline-none"
              />
            </div>
            <div>
              <label className="block font-cormorant text-cream/70 text-base mb-1">Date fin</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                className="w-full bg-dark-bg border border-gold-muted/30 text-cream p-2 rounded focus:border-sepia outline-none"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleApplyFilters}
                className="flex-1 bg-sepia text-dark-bg hover:bg-gold-soft font-cormorant py-2 px-4 rounded transition-colors"
              >
                Filtrer
              </button>
              <button
                onClick={handleResetFilters}
                className="bg-dark-bg border border-gold-muted/30 text-cream hover:bg-dark-secondary font-cormorant py-2 px-4 rounded transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center mb-4">
          <p className="font-cormorant text-cream/70">
            {transactions.length} transaction(s) trouvée(s)
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="bg-dark-card border border-red-500 text-red-400 hover:bg-red-500 hover:text-white font-cormorant py-2 px-6 rounded transition-colors flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Remise à zéro ({transactions.length})
            </button>
            <button
              onClick={handleExport}
              className="bg-dark-card border border-sepia text-sepia hover:bg-sepia hover:text-dark-bg font-cormorant py-2 px-6 rounded transition-colors flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Exporter JSON
            </button>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-dark-card border border-gold-muted/30 rounded overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-dark-secondary">
                <tr>
                  <th className="text-left font-playfair text-sepia p-4 text-base">Date</th>
                  <th className="text-left font-playfair text-sepia p-4 text-base">Type</th>
                  <th className="text-left font-playfair text-sepia p-4 text-base">Montant</th>
                  <th className="text-left font-playfair text-sepia p-4 text-base">Paiement</th>
                  <th className="text-left font-playfair text-sepia p-4 text-base">Statut</th>
                  <th className="text-left font-playfair text-sepia p-4 text-base">Email</th>
                  <th className="text-left font-playfair text-sepia p-4 text-base">Session ID</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="p-8 text-center font-cormorant text-cream/70">
                      Chargement...
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-8 text-center font-cormorant text-cream/70">
                      Aucune transaction trouvée
                    </td>
                  </tr>
                ) : (
                  transactions.map((tx, index) => (
                    <tr key={tx.id || index} className="border-t border-gold-muted/10 hover:bg-dark-bg/50">
                      <td className="p-4 font-cormorant text-cream/90 text-base">
                        {formatDate(tx.created_at)}
                      </td>
                      <td className="p-4 font-cormorant text-cream/90 text-base">
                        {tx.consultation_type === 'consultation_humaine' ? '👤 Humaine' : 
                         tx.consultation_type === 'consultation_animale' ? '🐾 Animale' : 
                         tx.consultation_type || '-'}
                      </td>
                      <td className="p-4 font-playfair text-sepia">
                        {tx.amount?.toFixed(2)} €
                      </td>
                      <td className="p-4">
                        <StatusBadge status={tx.payment_status} />
                      </td>
                      <td className="p-4">
                        <StatusBadge status={tx.status} />
                      </td>
                      <td className="p-4 font-cormorant text-cream/70 text-base">
                        {tx.email || tx.metadata?.email || '-'}
                      </td>
                      <td className="p-4 font-mono text-sm text-cream/50">
                        {tx.session_id?.substring(0, 20)}...
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
          </>
        )}

        {/* CONTACTS TAB */}
        {activeTab === 'contacts' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <h2 className="font-playfair text-xl text-sepia">Messages reçus</h2>
              <div className="flex items-center gap-4 flex-wrap">
                <p className="font-cormorant text-cream/60">
                  {contacts.filter(c => !c.is_read).length} non lu(s) / {contacts.length} total
                </p>
                <button
                  onClick={handleExportContactsCSV}
                  disabled={contacts.length === 0}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-sepia/40 bg-sepia/10 text-sepia hover:bg-sepia hover:text-dark-bg disabled:opacity-40 disabled:cursor-not-allowed font-lato text-sm tracking-wider uppercase transition-all"
                  data-testid="export-contacts-csv-btn"
                  title="Télécharger un fichier CSV (compatible Excel) de tous les contacts"
                >
                  <Download size={16} />
                  Télécharger CSV
                </button>
              </div>
            </div>

            {contacts.length === 0 ? (
              <div className="bg-dark-card border border-gold-muted/30 p-8 text-center">
                <p className="font-cormorant text-cream/60">Aucun message reçu</p>
              </div>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div 
                    key={contact.id} 
                    className={`bg-dark-card border p-6 ${
                      contact.is_read ? 'border-gold-muted/20' : 'border-sepia/50 bg-sepia/5'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-playfair text-lg text-cream flex items-center gap-2 flex-wrap">
                          {contact.prenom}
                          {contact.source === 'booking' ? (
                            <span className="px-2 py-1 bg-sepia/20 border border-sepia/40 text-sepia text-sm rounded font-cormorant">
                              🪦 Demande RDV
                              {contact.consultation_type === 'humain' && ' · 👤 Humain'}
                              {contact.consultation_type === 'animal' && ' · 🐾 Animal'}
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-cream/10 border border-cream/20 text-cream/70 text-sm rounded font-cormorant">
                              📧 Message
                            </span>
                          )}
                          {!contact.is_read && (
                            <span className="ml-1 px-2 py-1 bg-sepia text-dark-bg text-sm rounded">Nouveau</span>
                          )}
                        </h3>
                        <p className="font-cormorant text-cream text-xl font-medium mt-1">
                          <a href={`mailto:${contact.email}`} className="hover:text-sepia transition-colors">{contact.email}</a>
                          <span className="text-cream/40 mx-2">•</span>
                          {contact.telephone ? (
                            <a href={`tel:${contact.telephone.replace(/\s/g,'')}`} className="hover:text-sepia transition-colors">{contact.telephone}</a>
                          ) : (
                            <span className="text-cream/50 italic">Pas de téléphone</span>
                          )}
                        </p>
                        {(contact.ville || contact.departement) && (
                          <p className="font-cormorant text-cream/60 text-base">
                            {contact.ville}{contact.ville && contact.departement ? ', ' : ''}{contact.departement}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {!contact.is_read && (
                          <button
                            onClick={() => handleMarkContactRead(contact.id)}
                            className="text-green-400 hover:text-green-300 text-base font-cormorant"
                          >
                            ✓ Marquer lu
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteContact(contact.id)}
                          className="text-red-400 hover:text-red-300 text-base font-cormorant"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                    <div className="bg-dark-bg/50 p-4 border border-gold-muted/10">
                      <p className="font-cormorant text-cream/80 whitespace-pre-wrap">{contact.message}</p>
                    </div>
                    {contact.photo_base64 && (
                      <div className="mt-3 bg-dark-bg/40 p-3 border border-sepia/30">
                        <p className="font-cormorant text-cream/70 text-sm mb-2 flex items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-sepia" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Photo jointe {contact.photo_filename ? `: ${contact.photo_filename}` : ''}
                        </p>
                        <a href={contact.photo_base64} target="_blank" rel="noopener noreferrer" className="block">
                          <img
                            src={contact.photo_base64}
                            alt="Photo jointe par le visiteur"
                            className="max-h-72 max-w-full object-contain border border-gold-muted/30 hover:border-sepia transition-colors cursor-zoom-in"
                          />
                        </a>
                        <p className="mt-2 font-cormorant text-cream/40 text-sm italic">
                          Cliquez sur l'image pour l'afficher en plein écran.
                        </p>
                      </div>
                    )}
                    <p className="mt-3 font-cormorant text-cream/40 text-sm">
                      Reçu le {new Date(contact.created_at).toLocaleDateString('fr-FR', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* NEWSLETTER TAB */}
        {activeTab === 'newsletter' && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-dark-card border border-gold-muted/30 p-4 rounded">
                <p className="font-cormorant text-cream/70 text-base">Total abonnés</p>
                <p className="font-playfair text-2xl text-cream">{newsletter.stats?.total || 0}</p>
              </div>
              <div className="bg-dark-card border border-green-500/30 p-4 rounded">
                <p className="font-cormorant text-green-400 text-base">Actifs</p>
                <p className="font-playfair text-2xl text-green-400">{newsletter.stats?.active || 0}</p>
              </div>
              <div className="bg-dark-card border border-red-500/30 p-4 rounded">
                <p className="font-cormorant text-red-400 text-base">Inactifs</p>
                <p className="font-playfair text-2xl text-red-400">{newsletter.stats?.inactive || 0}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center">
              <h2 className="font-playfair text-xl text-sepia">Liste des abonnés</h2>
              <button
                onClick={handleExportNewsletter}
                className="bg-dark-card border border-sepia text-sepia hover:bg-sepia hover:text-dark-bg font-cormorant py-2 px-6 rounded transition-colors flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Exporter emails
              </button>
            </div>

            {/* Table */}
            <div className="bg-dark-card border border-gold-muted/30 rounded overflow-hidden">
              <table className="w-full">
                <thead className="bg-dark-secondary">
                  <tr>
                    <th className="text-left font-playfair text-sepia p-4 text-base">Email</th>
                    <th className="text-left font-playfair text-sepia p-4 text-base">Date d'inscription</th>
                    <th className="text-left font-playfair text-sepia p-4 text-base">Statut</th>
                    <th className="text-left font-playfair text-sepia p-4 text-base">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {newsletter.subscribers?.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="p-8 text-center font-cormorant text-cream/70">
                        Aucun abonné
                      </td>
                    </tr>
                  ) : (
                    newsletter.subscribers?.map((sub) => (
                      <tr key={sub.id} className="border-t border-gold-muted/10 hover:bg-dark-bg/50">
                        <td className="p-4 font-cormorant text-cream/90">{sub.email}</td>
                        <td className="p-4 font-cormorant text-cream/70 text-base">
                          {new Date(sub.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-sm ${
                            sub.is_active 
                              ? 'bg-green-600 text-white' 
                              : 'bg-red-600 text-white'
                          }`}>
                            {sub.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => handleDeleteSubscriber(sub.id)}
                            className="text-red-400 hover:text-red-300 text-base font-cormorant"
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && (
          <ChangePasswordCard />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-dark-secondary border-t border-gold-muted/30 px-6 py-4 mt-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-cormorant text-cream/50 text-base">
            Dashboard Admin - BRICEMEDIUM.FR © {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
};

// =============================================================
// Blog Page Wrapper (reuses Navigation + Footer + Modals)
// =============================================================
const BlogPage = ({ variant = "index" }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCGVOpen, setIsCGVOpen] = useState(false);
  const [isCGUOpen, setIsCGUOpen] = useState(false);
  const [isMentionsOpen, setIsMentionsOpen] = useState(false);
  const [isConfidentialiteOpen, setIsConfidentialiteOpen] = useState(false);
  const [isFAQOpen, setIsFAQOpen] = useState(false);
  const [isConditionsValidationOpen, setIsConditionsValidationOpen] = useState(false);

  return (
    <div>
      <Navigation
        onOpenModal={() => setIsModalOpen(true)}
        onOpenFAQ={() => setIsFAQOpen(true)}
      />
      {variant === "index" ? (
        <BlogIndexContent onOpenModal={() => setIsModalOpen(true)} />
      ) : (
        <BlogArticleContent onOpenModal={() => setIsModalOpen(true)} />
      )}
      <Footer
        onOpenCGV={() => setIsCGVOpen(true)}
        onOpenCGU={() => setIsCGUOpen(true)}
        onOpenMentions={() => setIsMentionsOpen(true)}
        onOpenConfidentialite={() => setIsConfidentialiteOpen(true)}
        onOpenFAQ={() => setIsFAQOpen(true)}
        onOpenConditionsValidation={() => setIsConditionsValidationOpen(true)}
      />
      <BackToTopButton />
      <BookingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <CGVModal isOpen={isCGVOpen} onClose={() => setIsCGVOpen(false)} />
      <CGUModal isOpen={isCGUOpen} onClose={() => setIsCGUOpen(false)} />
      <MentionsLegalesModal isOpen={isMentionsOpen} onClose={() => setIsMentionsOpen(false)} />
      <ConfidentialiteModal isOpen={isConfidentialiteOpen} onClose={() => setIsConfidentialiteOpen(false)} />
      <FAQModal isOpen={isFAQOpen} onClose={() => setIsFAQOpen(false)} />
      <ConditionsValidationModal isOpen={isConditionsValidationOpen} onClose={() => setIsConditionsValidationOpen(false)} />
    </div>
  );
};

// =============================================================
// Partenaires Page Wrapper (reuses Navigation + Footer + Modals)
// =============================================================
const PartenairesPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCGVOpen, setIsCGVOpen] = useState(false);
  const [isCGUOpen, setIsCGUOpen] = useState(false);
  const [isMentionsOpen, setIsMentionsOpen] = useState(false);
  const [isConfidentialiteOpen, setIsConfidentialiteOpen] = useState(false);
  const [isFAQOpen, setIsFAQOpen] = useState(false);
  const [isConditionsValidationOpen, setIsConditionsValidationOpen] = useState(false);

  return (
    <div>
      <Navigation
        onOpenModal={() => setIsModalOpen(true)}
        onOpenFAQ={() => setIsFAQOpen(true)}
      />
      <PartenairesContent />
      <Footer
        onOpenCGV={() => setIsCGVOpen(true)}
        onOpenCGU={() => setIsCGUOpen(true)}
        onOpenMentions={() => setIsMentionsOpen(true)}
        onOpenConfidentialite={() => setIsConfidentialiteOpen(true)}
        onOpenFAQ={() => setIsFAQOpen(true)}
        onOpenConditionsValidation={() => setIsConditionsValidationOpen(true)}
      />
      <BackToTopButton />
      <BookingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <CGVModal isOpen={isCGVOpen} onClose={() => setIsCGVOpen(false)} />
      <CGUModal isOpen={isCGUOpen} onClose={() => setIsCGUOpen(false)} />
      <MentionsLegalesModal isOpen={isMentionsOpen} onClose={() => setIsMentionsOpen(false)} />
      <ConfidentialiteModal isOpen={isConfidentialiteOpen} onClose={() => setIsConfidentialiteOpen(false)} />
      <FAQModal isOpen={isFAQOpen} onClose={() => setIsFAQOpen(false)} />
      <ConditionsValidationModal isOpen={isConditionsValidationOpen} onClose={() => setIsConditionsValidationOpen(false)} />
    </div>
  );
};

// Main Home Page
const HomePage = () => {
  const location = useLocation();
  const [showPreloader, setShowPreloader] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCGVOpen, setIsCGVOpen] = useState(false);
  const [isCGUOpen, setIsCGUOpen] = useState(false);
  const [isMentionsOpen, setIsMentionsOpen] = useState(false);
  const [isConfidentialiteOpen, setIsConfidentialiteOpen] = useState(false);
  const [isAjouterPhotoOpen, setIsAjouterPhotoOpen] = useState(false);
  const [isFAQOpen, setIsFAQOpen] = useState(false);
  const [isConditionsValidationOpen, setIsConditionsValidationOpen] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'cancelled', 'checking', null
  const [paymentMessage, setPaymentMessage] = useState('');

  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const newTheme = prev === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', newTheme);
      return newTheme;
    });
  }, []);

  // URL path -> section ID mapping for clean URLs
  const PATH_TO_SECTION = {
    '/jesuis': 'about',
    '/deroulement': 'process',
    '/services': 'services',
    '/temoignages': 'testimonials',
    '/hommages': 'mur-du-souvenir',
  };

  // SEO: per-route meta (canonical, title, description) so Google indexes each URL distinctly
  const PATH_META = {
    '/': {
      title: 'Je Parle avec les Défunts | Brice Médium',
      description: "Médium spécialisé : communication avec les morts & animaux décédés. 10 premières minutes gratuites.",
    },
    '/jesuis': {
      title: 'Qui suis-je — Brice Médium, 23 ans d\'expérience',
      description: "Découvrez Brice, médium et messager guidant les messages entre les mondes depuis plus de 23 ans. Plus de 11 000 consultations.",
    },
    '/deroulement': {
      title: 'Déroulement d\'une consultation — Brice Médium',
      description: "Découvrez le déroulement d'une consultation médiumnique : prise de contact, séance, suivi. 10 premières minutes offertes.",
    },
    '/services': {
      title: 'Services — Consultation Humaine & Animale — Brice Médium',
      description: "Services proposés par Brice Médium : communication avec les défunts humains (800€) et animaux décédés (800€). 10 min gratuites.",
    },
    '/temoignages': {
      title: 'Témoignages clients — Brice Médium',
      description: "Lisez les témoignages authentiques de personnes ayant consulté Brice Médium pour entrer en contact avec leurs proches disparus.",
    },
    '/hommages': {
      title: 'Hommages aux défunts — Mur du souvenir — Brice Médium',
      description: "Un mur du souvenir pour rendre hommage aux personnes disparues. Photos, prénoms et mémoires de ceux qui nous ont quittés.",
    },
  };

  // Apply per-route meta tags dynamically
  useEffect(() => {
    const meta = PATH_META[location.pathname] || PATH_META['/'];
    // Anchor-scroll routes share the same body HTML as homepage; consolidate canonical to '/'
    // to avoid Google "duplicate page" warnings in Search Console.
    const ANCHOR_ROUTES = ['/jesuis', '/deroulement', '/services', '/temoignages', '/hommages'];
    const canonicalPath = ANCHOR_ROUTES.includes(location.pathname) ? '/' : location.pathname;
    const canonicalUrl = `https://bricemedium.fr${canonicalPath === '/' ? '/' : canonicalPath}`;
    document.title = meta.title;

    const setMetaTag = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith('meta') ? 'meta' : 'link');
        const match = selector.match(/\[([^=]+)="([^"]+)"\]/);
        if (match) el.setAttribute(match[1], match[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMetaTag('meta[name="description"]', 'content', meta.description);
    setMetaTag('link[rel="canonical"]', 'href', canonicalUrl);
    setMetaTag('meta[property="og:title"]', 'content', meta.title);
    setMetaTag('meta[property="og:description"]', 'content', meta.description);
    setMetaTag('meta[property="og:url"]', 'content', canonicalUrl);
    setMetaTag('meta[name="twitter:title"]', 'content', meta.title);
    setMetaTag('meta[name="twitter:description"]', 'content', meta.description);
  }, [location.pathname]);

  // Scroll to corresponding section when URL changes
  useEffect(() => {
    const sectionId = PATH_TO_SECTION[location.pathname];
    if (sectionId && !showPreloader) {
      // Small delay to ensure section is rendered
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else if (location.pathname === '/' && !showPreloader) {
      // Scroll to top on home
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname, showPreloader]);

  // Check for Stripe payment return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');

    if (payment === 'success' && sessionId) {
      setPaymentStatus('checking');
      setPaymentMessage('Vérification du paiement en cours...');
      
      // Poll payment status
      const checkPaymentStatus = async (attempts = 0) => {
        const maxAttempts = 5;
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
        
        if (attempts >= maxAttempts) {
          setPaymentStatus('success');
          setPaymentMessage('Paiement confirmé ! Merci pour votre confiance. Votre consultation peut maintenant continuer.');
          return;
        }

        try {
          const response = await fetch(`${BACKEND_URL}/api/payments/status/${sessionId}`);
          if (response.ok) {
            const data = await response.json();
            if (data.payment_status === 'paid') {
              setPaymentStatus('success');
              setPaymentMessage('Paiement confirmé ! Merci pour votre confiance. Votre consultation peut maintenant continuer.');
              // Clear URL params
              window.history.replaceState({}, document.title, window.location.pathname);
              return;
            }
          }
          // Continue polling
          setTimeout(() => checkPaymentStatus(attempts + 1), 2000);
        } catch (error) {
          console.error('Error checking payment:', error);
          setTimeout(() => checkPaymentStatus(attempts + 1), 2000);
        }
      };

      checkPaymentStatus();
    } else if (payment === 'cancelled') {
      setPaymentStatus('cancelled');
      setPaymentMessage('Paiement annulé. Vous pouvez réessayer quand vous le souhaitez.');
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Check if cookie consent was already given
  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show cookie consent after preloader
      const timer = setTimeout(() => {
        if (!showPreloader) {
          setShowCookieConsent(true);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [showPreloader]);

  // Cookie consent handlers
  const handleAcceptCookies = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({ all: true, date: new Date().toISOString() }));
    setShowCookieConsent(false);
  };

  const handleRefuseCookies = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({ all: false, necessary: true, date: new Date().toISOString() }));
    setShowCookieConsent(false);
  };

  const handleCustomizeCookies = (preferences) => {
    localStorage.setItem('cookieConsent', JSON.stringify({ ...preferences, date: new Date().toISOString() }));
    setShowCookieConsent(false);
  };

  // Initialize theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, []);

  // Scroll reveal effect
  useEffect(() => {
    if (showPreloader) return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px"
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [showPreloader]);

  if (showPreloader) {
    return <Preloader onComplete={() => setShowPreloader(false)} />;
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <div className={theme === 'light' ? 'light-theme' : ''}>
        <Navigation onOpenModal={() => setIsModalOpen(true)} onOpenFAQ={() => setIsFAQOpen(true)} />
        <HeroSection onOpenModal={() => setIsModalOpen(true)} onOpenFAQ={() => setIsFAQOpen(true)} />
        <TransitionSection />
        <AboutSection />
        <GallerySection />
        <ProcessSection />
        <ConfidenceBanner />
        <ServicesSection />
        <TestimonialsSection />
        <MurDuSouvenir onOpenAjouterPhoto={() => setIsAjouterPhotoOpen(true)} />
        <NewsletterSection />
        <TryBanner />
        <CTASection onOpenModal={() => setIsModalOpen(true)} />
        <Footer onOpenCGV={() => setIsCGVOpen(true)} onOpenCGU={() => setIsCGUOpen(true)} onOpenMentions={() => setIsMentionsOpen(true)} onOpenConfidentialite={() => setIsConfidentialiteOpen(true)} onOpenFAQ={() => setIsFAQOpen(true)} onOpenConditionsValidation={() => setIsConditionsValidationOpen(true)} />
        <BackToTopButton />
        <BookingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        <CGVModal isOpen={isCGVOpen} onClose={() => setIsCGVOpen(false)} />
        <CGUModal isOpen={isCGUOpen} onClose={() => setIsCGUOpen(false)} />
        <MentionsLegalesModal isOpen={isMentionsOpen} onClose={() => setIsMentionsOpen(false)} />
        <ConfidentialiteModal isOpen={isConfidentialiteOpen} onClose={() => setIsConfidentialiteOpen(false)} />
        <FAQModal isOpen={isFAQOpen} onClose={() => setIsFAQOpen(false)} />
        <ConditionsValidationModal isOpen={isConditionsValidationOpen} onClose={() => setIsConditionsValidationOpen(false)} />
        <AjouterPhotoModal isOpen={isAjouterPhotoOpen} onClose={() => setIsAjouterPhotoOpen(false)} />
        <PaymentStatusModal 
          status={paymentStatus} 
          message={paymentMessage} 
          onClose={() => setPaymentStatus(null)} 
        />
        {showCookieConsent && (
          <CookieConsent 
            onAccept={handleAcceptCookies}
            onRefuse={handleRefuseCookies}
            onCustomize={handleCustomizeCookies}
            onOpenConfidentialite={() => setIsConfidentialiteOpen(true)}
          />
        )}
        <Toaster 
          position="top-center" 
          toastOptions={{
            style: {
              background: theme === 'dark' ? '#1a1a1a' : '#FDFCF8',
              border: '1px solid rgba(201, 169, 97, 0.3)',
              color: theme === 'dark' ? '#e8e8e8' : '#1a1a1a'
            }
          }}
        />
      </div>
    </ThemeContext.Provider>
  );
};

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/jesuis" element={<HomePage />} />
            <Route path="/deroulement" element={<HomePage />} />
            <Route path="/services" element={<HomePage />} />
            <Route path="/temoignages" element={<HomePage />} />
            <Route path="/hommages" element={<HomePage />} />
            <Route path="/blog" element={<BlogPage variant="index" />} />
            <Route path="/blog/:slug" element={<BlogPage variant="article" />} />
            <Route path="/partenaires" element={<PartenairesPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
